/**
 * Properties menu: clickable list of B2B, User or Event properties and cohorts to go inside mp-drop-menu
 */
import compact from 'lodash/compact';
import get from 'lodash/get';
import {nanoid} from 'nanoid';
import flatten from 'lodash/flatten';
import intersectionBy from 'lodash/intersectionBy';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import omit from 'lodash/omit';
import partition from 'lodash/partition';
import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import keyBy from 'lodash/keyBy';
import {AttrsSchema} from 'panel';
import isNil from 'lodash/isNil';
import pickBy from 'lodash/pickBy';
import stableStringify from 'json-stable-stringify';
import memoize from 'lodash/memoize';
import type {DOMPatcher} from 'panel/lib/dom-patcher';
import sum from 'lodash/sum';

import {debounce} from 'common/util/lodash-wrapped';
import {getNow} from 'common/util/timing';
import {
  DISABLED_DUE_TO_CLASSIFIED_DATA,
  MappedPropertyValues,
  Permission,
  ProjectFeatureFlag,
  PropertyType,
  ResourceType,
  SPECIAL_EVENTS,
  STRING_TYPECAST_PROPERTIES,
  TYPECAST_PROPERTY_TYPES,
} from 'common/report/constants';
import {
  eventDefinitionParamsAndKey,
  fetchAllPropertyDefinitionsForGroupProfiles,
  fetchGlobalEventPropertyDefinitions,
  fetchPropertyDefinitionsForEvents,
  fetchPropertyDefinitionsForUserProfiles,
  getAllGlobalPropertyDefinitions,
  getAllPropertyDefinitionsForEvents,
  getAllPropertyDefinitionsForUserProfiles,
  groupPropertyDefinitionsStore,
  isQueryOnAllEvents,
  peoplePropertyDefinitionsStore,
  propertyDefinitionsForEventsStore,
} from 'common/report/stores/property-definitions';
import {userDetailsStore} from 'common/report/stores/user-details';
import {assert, baseComparator, dataGroupIDsEqual, replaceByIndex} from 'common/util';
import {Cohort as CohortModel} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {
  isSerializedInlineCohort,
  SerializedCohort,
  SerializedInlineCohort,
  User,
} from 'common/query-builder/cohorts-query-builder/models/types/serialized-cohort-types';
import {promptCustomProperties} from 'common/widgets/custom-property-modal';
import {promptInlineCohorts} from 'common/widgets/inline-cohorts-modal/util';
import {INSIGHTS_PROPERTY_FILTER_RESOURCE_TYPE_MAPPING} from 'common/widgets/property-filter-menu/models/insights-filter';
import {eventPropertiesClient} from 'common/report/queries/query-api/event-properties-client';
import {peoplePropertiesClient} from 'common/report/queries/query-api/people-properties-client';
import {cohortsStore} from 'common/report/stores/cohorts';
import {customPropertiesStore, fetchCustomProperties} from 'common/report/stores/custom-properties';
import {currentProjectStore} from 'common/report/stores/project-details';
import {getIconForPropertyType, getLabelForPropertyType, renamePropertyValue} from 'common/report/util/properties';
import {Cohort, PropertyDefinition} from 'common/util/definitions-types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {
  getInlineCustomPropertyId,
  isBehavioralCustomProperty,
  isInlineCustomProperty,
  isPersistedCustomProperty,
} from 'common/widgets/custom-property-modal/util';
import {promptSharing} from 'common/widgets/sharing';
import {ContextPanelData} from 'common/widgets/context-panel/types';
import {getTooltipDataForCohort, getTooltipDataForProperty} from 'common/widgets/context-panel/util';
import {SavedCohortItem} from 'common/query-builder/query-entry-section/types';
import {SharableEntityType} from 'common/report/util/saved-entities/types';
import {isAllowedSuperadminOverride} from 'common/report/util/saved-entities';
import {
  eventPrefix as shareEventPrefix,
  ShareButtonUISource,
  track as shareTrack,
} from 'common/widgets/sharing/tracking';
import {
  getTempLookupTableMappings,
  LookupTableProperty,
  lookupTablesStore,
  registerTempLookupTableMapping,
} from 'common/report/stores/lookup-tables';
import {
  ExpanderItemClickEvent,
  SearchMatchResult,
  Section,
  SectionItem,
  SelectAllMechanism,
  SubLabelTheme,
} from 'common/components/items-menu/types';
import {InlineCohortsModalCommitEvent} from 'common/widgets/inline-cohorts-modal/types';
import {reporter} from 'common/report/error-reporting';
import {assertUnreachable} from 'common/util/typescript';
import {MPProperties, PATH_SEPARATOR} from 'common/constants';
import {chameleonReadyPromise, showTour, TourId} from 'common/util/chameleon';
import {DummyStorageManager} from 'common/contexts/storage-manager/dummy';
import {SelectEvent, ToggleOption} from 'common/components/toggle/types';
import {propMenuPrefetchEnabled} from 'common/report/util/rollouts';
import {queryBuilderUtils} from 'common/query-builder/query-builder/util';
import {ChangeEvent} from 'common/components/select/types';
import {Placement} from 'common/components/tooltip/types';
import {captureWorkflowTiming} from 'common/util/workflow-timing';
import {measureExecutionTime, useStore} from 'common/util/component-helpers';
import {fuzzyMatch} from 'common/util/fuzzy-match';
import {OrderBy} from 'common/util/array';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {getPropertyPathPrefix} from 'common/query-builder/query-block/utils/property-util';
import {
  getMenuItemForBehavior,
  getPrefixForBehavior,
  hasDynamicLabel,
  isMultiAttributionBehavior,
} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {getProjectId} from 'common/report/util';
import {Behavior} from 'common/types/reports/query';
import {formatDataGroupLabel} from 'common/query-builder/query-entry-section/util';
import {FeatureRolloutName, FeatureRollouts} from 'common/report/util/feature-sampling';

import {EventsController} from '../contexts/events-controller';
import {EventItem} from '../events-menu/types';
import {filterToBookmarkableEventItem} from '../events-menu/util';
import {createContextPanelEventHandlers} from '../events-menu/context-panel';
import {InlineCohortSegment} from '../segment-menu/types';
import {ChildMenu, ChildMenuBaseAttrs, TooltipContextActionResult} from '../child-menu';
import {TRACKING_EVENTS} from '../constants';
import {getOpenedMenuComponent, OpenedMenuComponent, sortItemsByLabel} from '../utils/util';
import {
  calculatePropertiesIntersection,
  fetchAllLookupTableProperties,
  filterToBookmarkablePropertyItem,
  getDataDefinitionsPropertyMenuItem,
  getItemUniqueKey,
  getPropertyDefinitions,
  getSelectedPropertyLabel,
  getTooltipNameForPropertyItem,
  getTooltipPathForPropertyItem,
  inferStagedItemType,
  propertyItemToObjectPropertyItem,
  sanitizeCustomProperty,
  storeCustomPropertyToProperty,
} from './util';
import {TrackingEvent} from './tracking';
import {
  AllPropertiesTab,
  AllTab,
  CohortItem,
  DataGroupItem,
  EventsInPropertiesMenuTab,
  FilterMenuChangeEvent,
  FilterSectionItem,
  FilterValue,
  GetSortedMenuSections,
  InlineCohort,
  isCohort,
  isEventSectionItem,
  isInlineCohort,
  isProperty,
  isPropertySectionItem,
  isSavedCohort,
  Item,
  MenuItemType,
  PropertiesMenuAttrs,
  PropertiesMenuJsxProps,
  PropertiesMenuParams,
  Property,
  PropertyCombineMode,
  PropertyItemAction,
  PropertyItemPersistedIdentifierKeys,
  SavedCohort,
  SelectedPropertyItem,
  SelectedTab,
  Selection,
  SelectPropertyEvent,
  SortBy,
  SortMenuChangeEvent,
  SortSectionItem,
  SpecialActions,
  SpecialControl,
  StagedItemType,
  TypecastPillAction,
} from './types';
import {
  CohortExtras,
  CombinedSection,
  EventSection,
  EventSectionItem,
  PropertiesItemClickEvent,
  PropertyItemState,
  PropertySection,
  PropertySectionItem,
  SectionItemProperty,
} from './item-menu-types';
import {
  ALL_USERS_COHORT,
  AllFilter,
  DATASET_PROPERTY,
  DATETIME_UNIT_OPTION_SECTION_ITEMS,
  FIRST_EVENT_TIME_PROPERTY,
  LIST_LENGTH,
  LIST_PROPERTY_SUM,
  LIST_PROPERTY_UNIQUES,
  OTHER_OPTIONS,
  SCD_EVENT_NAME,
  SORT_OPTION_ITEMS,
  SPECIAL_CONTROLS,
  SPECIAL_ORDER_PROPERTIES,
  SPECIAL_PROPERTIES,
  TOP_PEOPLE,
} from './constants';
import {template} from './template';
import css from './index.inline.styl';

// number of items to show per section on the "All" tab
// remaining items are hidden under a `Show all` expander
const MENU_ITEM_LIMIT = 10;

enum IncludeCustomPropertyType {
  All = `all`,
  None = `none`,
  PersistedOnly = `persisted-only`,
  PersistedNonBehavioralOnly = `persisted-non-behavioral-only`,
}

interface PropertiesMenuState {
  dataGroups: {[id: string]: DataGroupItem};
  permissionList: any;
  loadingCohorts: boolean;
  loadingEventProperties: boolean;
  loadingGlobalEventProperties: boolean;
  loadingPeopleProperties: boolean;
  selectedTab: SelectedTab;
  stagedItemsStack: PropertySectionItem[];
  stagedItemType: StagedItemType | null;
  searchFilter: string;
  getSortedMenuSections: GetSortedMenuSections;
  sortByValue: SortBy;
  isLoading: boolean;
  selectedFilter: FilterValue;
}

const _PAGE_EVENT_LOAD_HISTORY = new Set();
export class PropertiesMenu extends ChildMenu<
  PropertiesMenuState,
  PropertiesMenuAttrs,
  Selection,
  PropertiesMenuParams
> {
  private _context = null;
  private _cohortStoreCallback = null;
  private _lookupTablesStoreCallback = null;
  private _cachedSections: CombinedSection[] = null;
  private _cachedEventProperties: PropertyDefinition[] = null;
  private _recentlyUsedUniqueKeys: Set<string> = null;

  /** for composite menu flows (e.g. selecting an event + property) we allow external control for the back button on this menu */
  public backButtonHandler: () => void = null;

  /** perf tracking properties */
  private domPatcher: DOMPatcher;
  private _openedAt: number;
  private _openCount = 0;
  private _instanceEventLoadHistory = new Set();
  private _trackNextRenderWithProps: {[property: string]: any};
  // We compute this right after calculating menu sections. We store it in a property rather than relying on computing
  // it at tracking time because tracking happens async at which point cached sections could've been invalidated
  // already
  private _numTotalItemsOnPrevRender: number;

  static get attrsSchema(): AttrsSchema<ChildMenuBaseAttrs<Selection> & PropertiesMenuAttrs> {
    return {
      ...super.attrsSchema,
      'cohort-multiselect': `boolean`,
      'cohort-operation-type': `string`,
      'apply-button-text': {
        type: `string`,
        default: null,
      },
      'min-selection': {
        type: `number`,
        default: 0,
      },
      'allow-behavioral-property-creation': {
        type: `boolean`,
        description: `shows the 'Create behavioral property...' menu item in supported reports`,
      },
      'allow-typecast-pill-action': {
        type: `string`,
        description: `allows showing a pill labeled typecast next to eligible properties`,
        enum: Object.values(TypecastPillAction),
        default: null,
      },
      'enable-workspace-filter-restrictions': `boolean`,
      'disabled-items': {
        type: `json`,
        default: {}, // dict for fast inclusion lookups
        description: `sets disabled options for the underlying mp-items-menu`,
      },
      'include-custom-properties': {
        type: `string`,
        enum: Object.values(IncludeCustomPropertyType),
        default: IncludeCustomPropertyType.None,
        description: `whether to hide custom properties from properties menus`,
      },
      'exclude-dimension-properties': {
        type: `boolean`,
        description: `whether to hide dimension properties from properties menus`,
      },
      'exclude-lookup-table-properties': {
        type: `boolean`,
        description: `whether to hide user uploaded properties from a lookup table.
        Different from 'exclude-dimension-properties' since this still shows properties as mapped to a lookup table`,
      },
      'property-combine-mode': {
        type: `string`,
        default: PropertyCombineMode.Union,
        description: `defines how event properties should be merged`,
        enum: Object.values(PropertyCombineMode),
      },
      'show-all-tab': {
        type: `boolean`,
        description: `enables the "All" tab in the selection menu when there is more than one section`,
      },
      'show-all-users-cohort': {
        type: `boolean`,
        description: `enables the "All Users" cohort if cohort selection is enabled`,
      },
      'show-items-caret': {
        type: `boolean`,
        description: `enables the a caret icon in the selection items`,
      },
      'single-data-group': {
        type: `string`,
        description: `if set, shows group properties for a specific data group id`,
      },
      'other-options': {
        type: `json`,
        default: [],
        description: `list of items to show in the other section`,
      },
      'property-of-label': {
        type: `string`,
        description: `the label of the entity this is a property of (e.g. event name)`,
      },
      'show-select-all': `boolean`,
      'select-all-mechanism': {
        type: `string`,
        enum: Object.values(SelectAllMechanism),
        default: SelectAllMechanism.Full,
      },
      'scd-poc-enabled': `boolean`,
      'project-is-free-plan': `boolean`,
      'select-all-disabled': `boolean`,
      'hide-recently-viewed': `boolean`,
      'hide-section-labels': `boolean`,
      'selectable-sensitive-properties': `boolean`,
      'description-edit-disabled': `boolean`,
      'menu-item-types': {
        type: `json`,
        description: `defines what type of items this menu will render (events, cohorts, user props, etc)`,
        default: [],
      },
      'no-rounded-corners': {
        type: `boolean`,
        description: `control to make the button's rounded corner style`,
      },
      'type-filter': {
        type: `json`,
        description: `only show properties with these types`,
        default: [],
      },
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,

      contexts: [`eventsController` as const, `sessionStorageManager` as const, `currentProject` as const],
      defaultContexts: {
        eventsController: new EventsController(),
        sessionStorageManager: new DummyStorageManager(),
      },

      defaultState: {
        dataGroups: {},
        permissionList: [],
        isLoading: false,
        loadingCohorts: false,
        loadingEventProperties: false,
        loadingGlobalEventProperties: false,
        loadingPeopleProperties: false,
        selectedTab: AllTab,
        selected: null,
        // If stagedItemType is null, it means it's at the top level
        stagedItemType: null,
        searchFilter: ``,
        getSortedMenuSections: null,
        stagedItemsStack: [],
        openedAt: null,
        sortByValue: SortBy.Relevance,
        loading: false,
        selectedFilter: AllFilter.value,
      },
      params: {
        listProperty: Object,
      },
      helpers: {
        ...super.config.helpers,
        StagedItemTypes: StagedItemType,
        getMenuSections: () => {
          const usingCachedSections = Boolean(this._cachedSections);
          return measureExecutionTime(
            this,
            () => {
              const sections = this.getMenuSections();
              this._numTotalItemsOnPrevRender = sum(sections.map((section) => section.items.length));
              return sections;
            },
            {
              additionalProps: () => {
                return {
                  fn: `getMenuSections`,
                  usingCachedSections,
                  numTotalItems: this._numTotalItemsOnPrevRender,
                  isBetaMenu: this.isBetaMenu(),
                };
              },
            },
          );
        },
        contextPanelTransform: (focusedItem: PropertySectionItem | EventSectionItem) => {
          // Since event and properties menu item have different data structures
          // if an item is an event, section does not have a type. Instead, focusedItem.menuItemTyp has the type we are looking for
          const focusedItemType = focusedItem.menuItemType;
          let tooltipData: ContextPanelData = null;
          if (focusedItemType === MenuItemType.Events) {
            // TODO: need to figure out how to get recently used events from the events-menu
            tooltipData = this.getContext(`eventsController`).getContextPanelData(focusedItem.value, [], {
              allowEdit: !this.attr(`readonly`) && !this.attr(`hide-edit-controls`),
              isBetaMenu: this.isBetaMenu(),
              throughPropertiesMenu: true,
            });
          } else {
            const propertyItem = focusedItem as PropertySectionItem;
            tooltipData = propertyItem.tooltipData || this.getTooltipData(propertyItem, propertyItem.resourceType);
            if (tooltipData && !tooltipData.entityIcon) {
              tooltipData.entityIcon = propertyItem.icon;
            }
          }

          // default tooltipData for entries that do not have definitions
          if (tooltipData === null && !focusedItem.itemFromInput) {
            tooltipData = {
              header: focusedItem?.label,
              subHeader: null,
              path: focusedItem.subLabel ? [focusedItem.subLabel.split(PATH_SEPARATOR)[0]] : null,
              createdBy: null,
              entityIcon: focusedItem?.icon,
              trackedAs: null,
              description: ``,
              editItem: null,
              metrics: [],
              details: [],
              links: [],
              actions: [],
              isRecentlyUsed: focusedItem.isRecentlyUsed,
            };
          }

          return tooltipData;
        },
        handleClickItem: async ({detail: {item, section, ...detail}}: PropertiesItemClickEvent) => {
          // Special Action - An item presents a Create Modal
          const isSpecialAction = Object.values(SpecialActions).includes(item.value);
          if (isSpecialAction) {
            // Todo: Type this as a new section
            this.showCreateItemModal(item.value);
            return;
          }

          // for the beta menu, we set the `menuItemType` on the item itself when we merge all of the sections to a single section
          // for the classic menu, we still have access to the original section so we get the type from there.
          const menuItemType = item.menuItemType || section.type;
          let itemValue = item;

          // Stageable Item - An item keeps the user in the menu showing a new screen
          if (section.type !== MenuItemType.Events) {
            const propertyItem = item as PropertySectionItem;
            const stagedItemType = inferStagedItemType(propertyItem, this.helpers.useMultipleSelection());
            if (stagedItemType && this.canStageItemType(stagedItemType)) {
              this.updateStagedItem(item as PropertySectionItem, stagedItemType);
              return;
            } else if (stagedItemType === StagedItemType.Datetime) {
              // if the datetime stage is being prevented, auto-select day
              itemValue = {...(item as PropertySectionItem), unit: TimeUnit.Day};
            }
          }

          // List item that needs a subtype
          const isListProperty =
            (item as SectionItemProperty).type === PropertyType.List &&
            [MenuItemType.EventProperties, MenuItemType.UserProperties, MenuItemType.UserCustomProperties].includes(
              item.menuItemType,
            );
          const isCartAnalysisEnabled = FeatureRollouts[FeatureRolloutName.CartAnalysis].isEnabled(
            this.getContext(`currentProject`).get()?.value,
          );
          // lookup table property does not support `toptype` query so exclude those
          if (isListProperty && isCartAnalysisEnabled && !(item as SectionItemProperty).isLookupTableProperty) {
            const property = item as SectionItemProperty;

            this.update({isLoading: true});

            try {
              // look up list property subtype
              const propertyName = property.value;
              const client =
                property.resourceType === ResourceType.User ? peoplePropertiesClient : eventPropertiesClient;
              const queryParams = {
                projectId: getProjectId(),
                property: {
                  resourceType: property.resourceType,
                  propertyType: PropertyType.List,
                  propertyName,
                  propertyDefaultType: PropertyType.List,
                },
              };

              const properties =
                client.getCachedResponse(queryParams) || (await client.query(queryParams, {forceCache: true}));

              const rawSubType = properties.find((property) => property.name === propertyName)?.type;

              // make sure the type we get back is valid and default to string if we can't determine it
              const subType = Object.values(PropertyType).includes(rawSubType)
                ? (rawSubType as PropertyType)
                : PropertyType.String;

              property.joinPropertyType = PropertyType.List;
              property.type = subType;
              property.propertyType = subType;
              property.propertyDefaultType = subType;
              if (subType === PropertyType.Datetime) {
                property.unit = TimeUnit.Day;
              }
            } finally {
              // if the lookup fails it will just be treated like a string using previous behavior

              this.update({isLoading: false});
            }
          }

          // Default unknown property to string
          const isUnknownProperty = (itemValue as SectionItemProperty).propertyDefaultType === PropertyType.Unknown;
          if (isUnknownProperty) {
            (itemValue as SectionItemProperty).type = PropertyType.String;
            (itemValue as SectionItemProperty).propertyDefaultType = PropertyType.String;
          }

          // Selectable Item - An item is committed to the Query
          if (this.hasStagedItem()) {
            itemValue = this.applyContextFromStagedItems(itemValue);
          }
          itemValue = menuItemType === MenuItemType.Events ? itemValue.value : itemValue;
          this.selectItem(itemValue, {
            ...detail,
            commit: true,
            onItemClicked: true,
            menuItemType,
          });
        },
        getFilterOptionSectionItems: (): FilterSectionItem[] => {
          const hasAllFilter = !this.attr(`hide-all-properties-select`);
          const hasEvents = this.attr(`menu-item-types`).includes(MenuItemType.Events);
          const hasEventProps = this.shouldShowEventProps();
          const hasUserProps = this.shouldShowUserProps();
          const hasCohorts = this.shouldShowCohorts();
          const tooltipMaxWidth = `160px`;
          const visibleDataGroups = (this.getVisibleDataGroups() || []).map((dg) => ({
            label: `${dg.displayName.toLowerCase()} properties`,
            value: {resourceType: ResourceType.User, dataGroupId: dg.groupID, value: `data-group-${dg.groupID}`},
            tooltip: {
              placement: Placement.Right,
              text: `Properties describing details of a group profiles. \n[Learn more](https://docs.mixpanel.com/docs/analysis/advanced/group-analytics)`,
            },
          }));
          return [
            hasAllFilter
              ? {
                  ...AllFilter,
                  label: `all properties`,
                  tooltip: {
                    placement: Placement.Right,
                    text: `All including tracked and virtual properties`,
                    'max-width': tooltipMaxWidth,
                  },
                }
              : null,
            hasEventProps
              ? {
                  label: `event properties`,
                  value: {resourceType: ResourceType.Event, value: `event`},
                  tooltip: {
                    placement: Placement.Right,
                    text: `Properties describing details of events \n[Learn more](https://docs.mixpanel.com/docs/tracking/how-tos/events-and-properties)`,
                    'max-width': tooltipMaxWidth,
                  },
                }
              : null,
            hasUserProps
              ? {
                  label: `user properties`,
                  value: {resourceType: ResourceType.User, value: `user`},
                  tooltip: {
                    placement: Placement.Right,
                    text: `Properties describing details on user profiles \n[Learn more](https://docs.mixpanel.com/docs/tracking/how-tos/events-and-properties)`,
                    'max-width': tooltipMaxWidth,
                  },
                }
              : null,
            ...visibleDataGroups,
            hasEvents
              ? {
                  label: `users who did...`,
                  value: {resourceType: `events` as `events`, value: `events`},
                  tooltip: {
                    placement: Placement.Right,
                    text: `Virtual property describing groups of users defined by a chosen set of criteria, like a shared property or sequence of events.  \n[Learn more](https://docs.mixpanel.com/docs/analysis/advanced/group-analytics)`,
                    'max-width': tooltipMaxWidth,
                  },
                }
              : null,
            hasCohorts
              ? {
                  label: `cohorts`,
                  value: {resourceType: ResourceType.Cohort, value: `cohorts`},
                  tooltip: {
                    placement: Placement.Right,
                    text: `Virtual property describing groups of users defined by a chosen set of criteria, like a shared property or sequence of events. \n[Learn more](https://docs.mixpanel.com/docs/analysis/advanced/cohorts)`,
                    'max-width': tooltipMaxWidth,
                  },
                }
              : null,
          ]
            .filter(Boolean)
            .map((option) => ({
              ...option,
              isSelected: option.value.value === this.state.selectedFilter.value,
            }));
        },
        handleFilterChange: ({detail}: FilterMenuChangeEvent) => {
          const selectedFilter = detail.item.value;
          this.update({selectedFilter});
          this.dispatchTabChangeEvent(selectedFilter?.dataGroupId || selectedFilter?.resourceType || AllTab);
          this.trackEvent(TRACKING_EVENTS.CHANGED_FILTER, {filter: selectedFilter});
        },
        getSortingOptionSectionItems: (): SortSectionItem[] => {
          return SORT_OPTION_ITEMS.map((option) => ({
            ...option,
            isSelected: option.value === this.state.sortByValue,
          }));
        },
        handleSortChange: ({detail}: SortMenuChangeEvent) => {
          const sortByValue = detail.item.value;
          this.update({sortByValue});
          this.trackEvent(TRACKING_EVENTS.CHANGED_SORTING, {'sorting type': sortByValue});
        },

        handleSearchInput: (searchFilter) => this.debouncedSearchInput(searchFilter),

        onItemPillClick: (ev) => this.handleItemPillClick(ev.detail.item),
        handleSelectAll: () => {
          const mechanism = this.attr(`select-all-mechanism`);

          if (mechanism === SelectAllMechanism.Partial) {
            // Deselect All except for disabled items
            const selected = this.state.selected.filter((item) => get(this.attr(`disabled-items`), item.value, false));
            this.update({
              selected,
            });
          }
        },
        getCreateSectionItems: () => {
          const sectionItems = [];

          if (this.canCreateCustomEventProperties()) {
            sectionItems.push({
              icon: `event-attribute`,
              label: `Event Property`,
              value: SPECIAL_CONTROLS.CREATE_CUSTOM_EVENT_FORMULA.value,
            });
          }

          if (this.canCreateCustomUserProperties()) {
            sectionItems.push({
              icon: `profile-attribute`,
              label: `User Property`,
              value: SPECIAL_CONTROLS.CREATE_CUSTOM_PEOPLE_FORMULA.value,
            });
            if (this.isBehavioralPropertiesEnabled()) {
              sectionItems.push({
                label: `Behavioral Property`,
                value: SPECIAL_CONTROLS.CREATE_CUSTOM_BEHAVIORAL_PROPERTY.value,
              });
            }
          }

          if (this.canCreateInlineCohorts()) {
            sectionItems.push({
              icon: `community`,
              label: `Cohort`,
              value: SPECIAL_CONTROLS.CREATE_INLINE_COHORT.value,
            });
          }

          return sectionItems;
        },
        handleCreateItemClick: ({detail}: ChangeEvent<SpecialActions>) => this.showCreateItemModal(detail.item.value),
        triggerModal: (item) => this.showCreateItemModal(item.value),
        unstageProperty: () => this.updateStagedItem(null, null),
        useMultipleSelection: () =>
          this.attr(`multiple`) || (this.attr(`cohort-multiselect`) && this.isCohortSelection()),

        getDefaultFormulaName: () => {
          const entityNames = this.getCustomEntities([ResourceType.Event, ResourceType.User]).map((item) => item.value);
          const prefix = this.attr(`cohort-operation-type`)
            ? `Custom ${this.attr(`cohort-operation-type`)}`
            : `Custom formula`;
          for (let i = 1; ; i++) {
            const name = `${prefix} ${i}`;
            if (!entityNames.includes(name)) {
              return name;
            }
          }
        },

        tooltipHandlersForEventType: createContextPanelEventHandlers({
          track: this.trackEvent.bind(this),
          handleSelect: ({item}) => this.selectItem(item),
          handleDelete: () => {
            this.dispatchEvent(new CustomEvent(`delete`));
          },
          additionalArgs: {
            'all-data-groups': this.attr(`all-data-groups`),
            'project-has-people': this.attr(`project-has-people`),
            'track-name': this.attr(`track-name`),
          },
        }),

        handleTooltipContextEditDescription: (ev) => {
          if (this.state.selectedTab === EventsInPropertiesMenuTab) {
            this.helpers.tooltipHandlersForEventType.tooltipContextEditDescription(ev);
          } else {
            this.helpers.propagateEvent(ev);
          }
        },

        handleTooltipContextAction: (ev) => {
          const {name} = ev.detail;
          if (this.state.selectedTab === EventsInPropertiesMenuTab) {
            this.helpers.tooltipHandlersForEventType.tooltipContextAction(ev);
            return;
          }
          this.onModalOpen();
          if ([`shareCohort`, `shareCustomProperty`].includes(name)) {
            this.handleShareTooltipContextAction(ev);
          } else {
            const result = this.handleTooltipContextAction(ev);
            if (!result || !result.handled) {
              this.helpers.propagateEvent(ev);
            }
          }
        },

        handleTabSelect: ({detail}: SelectEvent) => {
          const selectedTab = detail.selected;
          this.update({selectedTab});
          this.trackEvent(TrackingEvent.SelectPropertiesDropdownOption, {
            selectedTab,
            context: this._context,
          });
          this.dispatchTabChangeEvent(selectedTab);
        },
        getStagedItemLabel: (separator = `.`) =>
          this.state.stagedItemsStack.map(({name}) => name || ``).join(separator),

        getHeaderLabel: () => {
          const label = this.helpers.getStagedItemLabel();

          switch (this.getStagedItemType()) {
            case StagedItemType.Typecast:
              return `${label} Typecast`;
            case StagedItemType.Datetime:
              return `Date Time Unit`;
            case StagedItemType.Dimension:
              return label;
            case StagedItemType.Object:
              return label;
            case StagedItemType.User:
            case StagedItemType.Group:
            case StagedItemType.Cohort:
              return this.helpers.getStagedItemLabel(` > `);
            default:
              return null;
          }
        },

        getBehaviorMenuSection: (stagedItem: PropertySectionItem): EventSection[] => {
          const section = this.getEventsSections(
            stagedItem,
            stagedItem.submenuItemType as MenuItemType.Events | MenuItemType.EventWithProperties,
            this.state.selected?.behavior?.event,
          )[0];
          return [
            {...section, items: this.sortProperties(section.items as PropertySectionItem[]) as EventSectionItem[]},
          ];
        },

        getObjectMenuSection: (): PropertySection[] => {
          let property;
          let forExperiments = false;

          // if we want to support unlimited nesting in the future, we can use getParents on the filter to get the whole list tree
          const listOfObjectsProperty = this.params.listProperty;

          if (listOfObjectsProperty) {
            property = {
              resourceType: listOfObjectsProperty.resourceType,
              propertyType: `list_object`,
              propertyName: listOfObjectsProperty.propertyName,
              propertyDefaultType: PropertyType.List,
            };
          } else {
            const [baseProperty, ...nestedProps] = this.state.stagedItemsStack.filter(
              (item) => isProperty(item) && !isNil(item.type), // filter out non-property items
            );

            property = propertyItemToObjectPropertyItem({
              ...baseProperty,
              ...(nestedProps.length
                ? {
                    propertyObjectKey: nestedProps.map((property) => property.value),
                    joinPropertyType: PropertyType.Object,
                  }
                : {}),
            });

            forExperiments = baseProperty.value === MPProperties.Experiments;
          }

          const client = property.resourceType === ResourceType.Event ? eventPropertiesClient : peoplePropertiesClient;
          const params = {
            [listOfObjectsProperty ? `property` : `objectProperty`]: property,
            projectId: this.sourceAttrs.projectId,
            workspaceId: this.sourceAttrs.workspaceId,
          };

          const properties = client.getCachedResponse(params);

          if (!properties) {
            // Asynchronously fetches or looks up nested object property in cache
            client.query(params, {forceCache: true}).then(() => this.update());

            return [
              {
                isLoading: true,
                items: [],
              },
            ];
          }

          const sections: PropertySection[] = [
            {
              items: this.getPropertyMenuItems({
                properties: properties.map((property) => {
                  return {
                    ...property,
                    label: forExperiments
                      ? renamePropertyValue(property.name, MappedPropertyValues.ExperimentId)
                      : property.label,
                    type: forExperiments ? PropertyType.String : property.type,
                    isSelected: false,
                  };
                }),
                resourceType: property.resourceType,
              }),
            },
          ];

          if (listOfObjectsProperty) {
            const computedListPropertiesSection = this.getComputedListProperties();
            if (computedListPropertiesSection) {
              sections.push(computedListPropertiesSection);
            }
          }

          return sections;
        },

        getGroupMenuSection: (item: PropertySectionItem) => {
          const dataGroupID = item.value.groupId;
          return [
            {
              items: this.getPropertyMenuItems({
                properties: this.state.dataGroups[dataGroupID].properties,
                resourceType: ResourceType.User,
              }),
            },
          ];
        },

        getCohortMenuSection: () => {
          return this.getCohortsMenuSections();
        },

        getDimensionMenuSection: (item: PropertySectionItem) => {
          assert(isProperty(item));
          const dataGroupId = item.tempDataGroupId || item.dimensionDataGroupId;
          const dimension = item.value;
          const lookupTableProperties = (lookupTablesStore.value[dataGroupId]?.properties || []).map(
            (property: LookupTableProperty): Property => {
              return {
                ...property,
                dataGroupId,
                propertyDefaultType: property.type,
                resourceType: item.resourceType,
                value: [dimension, property.name],
              };
            },
          );

          if (lookupTableProperties.length === 0) {
            reporter.error(`[lookup] properties-menu: blank lookup table shown`, {
              projectId: this.attr(`project-id`),
              workspaceId: this.attr(`workspace-id`),
              userId: this.attr(`user-id`),
              dataGroupId,
            });
          }

          const properties = [
            // Include the dim prop underlying property.
            {
              propertyDefaultType: item.propertyDefaultType,
              resourceType: item.resourceType,
              name: item.name,
              label: item.label,
              type: item.joinPropertyType,
              value: dimension,
              customProperty: item.customProperty,
              customPropertyId: item.customPropertyId,
            },
            ...lookupTableProperties,
          ];

          return [
            {
              items: this.getPropertyMenuItems({
                properties,
                resourceType: item.resourceType,
              }),
            },
          ];
        },

        getDatetimeMenuSections: () => {
          return [{items: DATETIME_UNIT_OPTION_SECTION_ITEMS}];
        },

        getTypecastMenuSections: ({type}) => {
          const typeCastTypes = [...TYPECAST_PROPERTY_TYPES, PropertyType.List];
          const types = [...typeCastTypes, ...(typeCastTypes.includes(type) ? [] : [type])];

          const items = types.map((typeCastType) => {
            return {
              type: typeCastType,
              icon: getIconForPropertyType(typeCastType),
              isSelected: typeCastType === type,
              label: getLabelForPropertyType(typeCastType),
              value: typeCastType,
            };
          });

          return [{items}];
        },

        sectionsFromInput: (input: string) => {
          const resourceTypeFilter = this.state.selectedFilter.resourceType || this.state.selectedTab;
          const items = [];

          if (this.hasStagedItem()) {
            return [];
          }
          const isBetaMenu = this.isBetaMenu();
          const label = isBetaMenu ? `"${input}"` : input;
          const icon = isBetaMenu ? null : `plus`;
          switch (resourceTypeFilter) {
            case EventsInPropertiesMenuTab:
              items.push({
                value: {value: input, label: input},
                icon,
                label,
                itemFromInput: true,
              });
              break;
            case ResourceType.Event:
              items.push({
                type: PropertyType.String,
                propertyDefaultType: PropertyType.String,
                value: input,
                isSelected: this.isItemSelected(input, this.state.selected),
                resourceType: resourceTypeFilter,
                icon,
                label,
                itemFromInput: true,
              });
              break;
            case ResourceType.User:
              items.push({
                type: peoplePropertyDefinitionsStore?.value[input]?.type || PropertyType.String,
                propertyDefaultType: peoplePropertyDefinitionsStore?.value[input]?.type || PropertyType.String,
                value: input,
                isSelected: this.isItemSelected(input, this.state.selected),
                resourceType: resourceTypeFilter,
                icon,
                label,
                itemFromInput: true,
              });
              break;
            default: {
              const resourceType = this.shouldShowEventProps()
                ? ResourceType.Event
                : this.canAccessUserProperties() && ResourceType.User;
              if (resourceType && isBetaMenu) {
                return [
                  {
                    items: [
                      {
                        resourceType,
                        type: PropertyType.String,
                        propertyDefaultType: PropertyType.String,
                        value: input,
                        label,
                        itemFromInput: true,
                      },
                    ],
                    hideTopDivider: true,
                  },
                ];
              }
              break;
            }
          }
          return [{items: items.filter(Boolean), type: resourceTypeFilter}];
        },

        deletedCustomProperty: () => {
          const selected = this.state.selected;
          assert(!Array.isArray(selected) && isProperty(selected));
          const selectedCustomPropertyId = selected.customPropertyId;
          // when filter menu is open, `selected` on the underlying
          // properties-menu is set to null, so we have to manually delete
          // ourselves here. the delete callback can handle anything else.
          if (!selectedCustomPropertyId) {
            this.dispatchEvent(new CustomEvent(`delete`));
          }
          this.onModalClose();
        },
        applyCustomProperty: (customProp, originalCustomPropId) => {
          // originalCustomPropId will be the same as customProp.customPropertyId
          // unless we are saving a temporary custom property for the first time
          // (in which case it will be the old temporary id) or we are creating
          // a custom property for the first time (in which case it will be null)
          let {resourceType, customProperty, customPropertyId} = customProp;
          // we use 'user' and 'event' in the new query builder
          if (Object.keys(INSIGHTS_PROPERTY_FILTER_RESOURCE_TYPE_MAPPING).includes(resourceType)) {
            resourceType = INSIGHTS_PROPERTY_FILTER_RESOURCE_TYPE_MAPPING[resourceType];
          }
          customProperty = sanitizeCustomProperty(customProperty);
          const property = {
            ...customProp,
            label: customProperty.name,
            customProperty,
            customPropertyId: isPersistedCustomProperty(customPropertyId) ? Number(customPropertyId) : customPropertyId,
            resourceType,
          };

          let selected = this.state.selected;
          if (!selected) {
            selected = [];
          } else if (!Array.isArray(selected)) {
            selected = [selected];
          }
          const index = selected.findIndex(
            (selectedItem) =>
              isProperty(selectedItem) &&
              (selectedItem.customPropertyId === property.customPropertyId ||
                selectedItem.customPropertyId === originalCustomPropId),
          );

          const selectedCustomProp = selected[index] as Property;
          const {joinPropertyType} = customProperty;
          if (joinPropertyType) {
            property.joinPropertyType = joinPropertyType;
            if (Array.isArray(selectedCustomProp.value)) {
              // dimension property selected
              property.dataGroupId = customProperty.mappedDataGroupId;
              property.value = [property.value, selectedCustomProp.value[1]];
              property.propertyDefaultType = selectedCustomProp.propertyDefaultType;

              property.type = selectedCustomProp.type;
              property.propertyType = selectedCustomProp.type;
            } else {
              property.type = joinPropertyType;
              property.propertyType = joinPropertyType;
            }
          }

          if (index > -1) {
            this.replaceItem(property, index, {commit: true});
          } else {
            this.selectItem(property, {commit: true});
          }
          this.onModalClose();
        },
        onApplyButtonClick: () => {
          if (!this.isApplyButtonDisabled()) {
            this.dispatchSelectEvent({
              item: this.getArraySelection(),
              rawItem: null,
              autoTriggered: false,
              onItemClicked: false,
              commit: true,
            });
          }
        },
        //#endregion
      },
    };
  }

  shouldCloseOnSelect() {
    return !this.shouldShowApplyButton();
  }

  customPropertiesStoreCallback = () => {
    this.getTooltipData.cache.clear();
    const selected = this.state.selected;
    if (!selected || isEmpty(selected) || Array.isArray(selected) || !isProperty(selected)) {
      return;
    }
    const selectedCustomPropertyId = selected.customPropertyId;
    if (selectedCustomPropertyId in customPropertiesStore.value) {
      const newCustomPropertyId = customPropertiesStore.value[selectedCustomPropertyId].newCustomPropertyId;

      const property = storeCustomPropertyToProperty(newCustomPropertyId || selectedCustomPropertyId);
      assert(isProperty(property));
      const selectedCustomProperty = sanitizeCustomProperty((selected || {}).customProperty);
      if (
        newCustomPropertyId ||
        (!isEmpty(selectedCustomProperty) && !isEqual(property.customProperty, selectedCustomProperty))
      ) {
        // update for breakdown menu and filter menus that are closed (the opened one has no `selected` set)
        const item = this.getPropertyMenuItem({
          property,
          resourceType: property.resourceType,
        });
        this.selectItem(item, {autoTriggered: true});
      }
    } else if (
      customPropertiesStore.hasFetchedValue() &&
      isPersistedCustomProperty(selectedCustomPropertyId) &&
      Object.keys(customPropertiesStore.value).length > 0
    ) {
      // delete
      this.dispatchEvent(new CustomEvent(`delete`));
    }
  };

  generateShowObjectPropertiesItemForKey = (key: string, resourceType: ResourceType): PropertySectionItem => {
    let menuItemType;
    switch (resourceType) {
      case ResourceType.Event:
        menuItemType = MenuItemType.EventProperties;
        break;
      case ResourceType.User:
        menuItemType = MenuItemType.UserProperties;
        break;
      case ResourceType.Cohort:
      case ResourceType.Other:
      case ResourceType.Formula:
        throw Error(`Unexpected resource type in generateShowObjectPropertiesItemForKey: ${resourceType}`);
      default:
        assertUnreachable(resourceType);
    }
    return {
      type: PropertyType.Object,
      propertyDefaultType: PropertyType.Object,
      label: key,
      name: key,
      resourceType,
      value: key,
      stagedItemType: StagedItemType.Object,
      menuItemType,
    };
  };

  public connectedCallback() {
    this.setOpenedAt();

    // Ensure `this.updateFromSelection()` is called before `fetchCohorts()` (which is also called in `fetchData()`)
    // because fetching cohorts can trigger item replacements by calling `replaceItem()`
    // which can misbehave if this.updateFromSelection() hasn't updated `this.state.selectedFilter` from its default value.
    //
    // It misbehaves because `replaceItem()` does different things depending on whether `this.helpers.useMultipleSelection()` is true,
    // and `this.helpers.useMultipleSelection()` depends on `this.state.selectedFilter`.
    this.update({
      selected: this.attr(`selected`),
    });
    this.updateFromSelection();

    // This is in case if we haven't already pre-fetched the dataGroupStore and group properties
    // TODO: (Akshay) - We should eventually convert all of these dataGroupStore calls into a separate model resource
    // and incorporate into the App's context
    useStore(this, dataGroupStore, () => this.loadDataGroups()).fetch({
      projectId: this.attr(`project-id`),
      workspaceId: this.attr(`workspace-id`),
    });

    this.fetchData();

    fetchCustomProperties({callback: this.customPropertiesStoreCallback});

    this.loadStoreValues();
    this.maybeNavigateToSelected();

    // Since super.connectedCallback() performs a render + DOM patch, code that updates state should happen before
    // super.connectedCallback() to avoid a re-render/patch.
    super.connectedCallback();

    /**
     * stopgap monkey-patch the dom-patcher to track true render times.
     * TODO: @Jordan Implement workflowTiming util once @RyanHirsch's PRs land:
     * - https://github.com/mixpanel/analytics/pull/37635
     * - https://github.com/mixpanel/panel/pull/132
     */
    const _render = this.domPatcher.render;
    this.domPatcher.render = () => {
      if (!this.domPatcher) {
        // this function is called in a RAF callback if async patching is set
        // check for connection before proceeeding
        return;
      }
      _render.apply(this.domPatcher);
      if (this._trackNextRenderWithProps) {
        this.trackEvent(TrackingEvent.MenuLoadedDomPatcher, {
          elapsedMs: getNow() - this._openedAt,
          ...this._trackNextRenderWithProps,
        });
        this._trackNextRenderWithProps = null;
      }
    };
  }

  public disconnectedCallback() {
    customPropertiesStore.unsubscribe(this.customPropertiesStoreCallback);
    propertyDefinitionsForEventsStore.unsubscribeAll(this.eventPropertiesDefinitionsStoreCallback);
    peoplePropertyDefinitionsStore.unsubscribeAll(this.userPropertiesDefinitionsStoreCallback);

    lookupTablesStore.unsubscribe(this._lookupTablesStoreCallback);

    if (this.attr(`project-has-cohorts`)) {
      const dataGroupId = this.attr(`data-group-id`);
      if (dataGroupId && this.attr(`support-b2b-cohorts`)) {
        cohortsStore.unsubscribe(this._cohortStoreCallback, dataGroupId);
      } else {
        cohortsStore.unsubscribe(this._cohortStoreCallback);
      }
    }
    super.disconnectedCallback();
  }

  public loadStoreValues() {
    // all properties-menus upload any inline custom properties they host.
    const inlineCustomProperties = flatten(
      this.getPropertyResourceTypes().map((type) => {
        return this.getSelectedCustomFormula(type);
      }),
    ).reduce((acc, cur) => {
      assert(isProperty(cur));
      const {customPropertyId, customProperty} = cur;
      return {
        ...acc,
        [getInlineCustomPropertyId(customPropertyId)]: customProperty,
      };
    }, {});

    customPropertiesStore.update(inlineCustomProperties);

    // load all inline cohorts into cohort store
    cohortsStore.update(
      this.getInlineCohortItems().reduce((acc, cur) => {
        const cohort: SerializedInlineCohort = {
          name: cur.label,
          created_by: cur.createdBy,
          raw_cohort: cur.value.cohort.raw_cohort,
        };
        return {
          ...acc,
          [cur.value.cohort.raw_cohort.unsavedId]: cohort,
        };
      }, {}),
    );
  }

  public resetStage() {
    this.update({
      stagedItemsStack: [],
      stagedItemType: null,
    });
  }

  public hasStagedItem(): boolean {
    return this.state.stagedItemsStack?.length > 0;
  }

  public peekStagedItem() {
    return this.state.stagedItemsStack[this.state.stagedItemsStack.length - 1];
  }

  /* A true value signifies that the properties-menu will render mp-input and updates the searchFilter attribute */
  public isStagedItemSearchable(): boolean {
    return this.hasStagedItem() && [StagedItemType.Dimension, StagedItemType.Object].includes(this.getStagedItemType());
  }

  /**
   * Sets the active tab to the section that the currently selected values belongs to.
   * If properties from multiple tabs are selected, sets the "All" tab when enabled, or to the first tab otherwise
   */
  public updateFromSelection() {
    const currentTab = this.state.selectedTab;
    let selected: Selection = this.state.selected || [];
    selected = Array.isArray(selected) ? selected : [selected];

    const singleSelection = selected[0];
    const resourceType = singleSelection?.resourceType;
    const dataGroupId = isProperty(singleSelection) && singleSelection.dataGroupId;
    const isCohort =
      Array.isArray(this.state.selected) &&
      (!resourceType || resourceType === ResourceType.Cohort) &&
      (!this.attr(`menu-item-types`) ||
        this.attr(`menu-item-types`).length === 0 ||
        this.attr(`menu-item-types`)?.includes[MenuItemType.Cohorts]);
    const isDimProp = this.isDimensionSelected(singleSelection);
    const isB2BTab = dataGroupId && !isDimProp;

    let selectedTab;
    let selectedFilter;
    if (this.isBetaMenu()) {
      if (this.attr(`hide-all-properties-select`)) {
        selectedFilter = this.helpers.getFilterOptionSectionItems()[0].value;
      }
      selectedTab = AllPropertiesTab;
    } else if (isCohort) {
      selectedTab = ResourceType.Cohort;
    } else if (isB2BTab) {
      selectedTab = dataGroupId;
    } else {
      selectedTab = resourceType;
    }
    const tabs = this.getToggleTabs();
    if (selectedTab) {
      if (resourceType === ResourceType.Other) {
        this.update({selectedTab: this.getDefaultToggleTab()});
        return;
      }
      this.update({selectedTab});

      if (selectedFilter) {
        this.update({selectedFilter});
      }
    } else if (tabs.length && !tabs.find((tab) => tab.value === this.getDefaultToggleTab())) {
      this.update({selectedTab: tabs[0].value});
    } else {
      this.update({selectedTab: this.getDefaultToggleTab()});
    }

    if (currentTab !== this.state.selectedTab) {
      this.dispatchTabChangeEvent(this.state.selectedTab);
    }

    if (this.isBetaMenu() && resourceType === ResourceType.Cohort) {
      this.update({selectedFilter: {resourceType: ResourceType.Cohort, value: `cohorts`}});
    }
  }

  maybeNavigateToSelected() {
    const selected = this.state.selected;
    const selectedItem = this.getSelectedItem();
    if (!selectedItem) {
      return;
    }

    if (this.isDimensionSelected(selected) && this.canStageItemType(StagedItemType.Dimension)) {
      // open dimension property submenu
      const property = this.getLookupTablePropertyFromSelected(selectedItem);
      if (property) {
        const item = this.getPropertyMenuItem({
          property,
          resourceType: selectedItem.resourceType,
        });
        this.updateStagedItem(item, StagedItemType.Dimension);
      }
    }

    // Unfurl/restore nested object properties to stagedItemsStack
    if (
      isProperty(selectedItem) &&
      selectedItem?.joinPropertyType === PropertyType.Object &&
      selectedItem?.propertyObjectKey?.length
    ) {
      [selectedItem.value, ...selectedItem.propertyObjectKey.slice(0, -1)].forEach((propertyKey) => {
        const propertyKeyString = Array.isArray(propertyKey) ? propertyKey[propertyKey.length - 1] : propertyKey;
        this.updateStagedItem(
          this.generateShowObjectPropertiesItemForKey(propertyKeyString, selectedItem.resourceType),
          StagedItemType.Object,
        );
      });
    } else if (isProperty(selectedItem) && selectedItem.behavior) {
      this.navigateToBehavior(selectedItem.behavior);
    } else if (
      this.params.listProperty &&
      isProperty(selectedItem) &&
      typeof selectedItem.value === `string` &&
      [LIST_PROPERTY_SUM.value, LIST_PROPERTY_UNIQUES.value].includes(selectedItem.value)
    ) {
      const stagedItem = selectedItem.value === LIST_PROPERTY_SUM.value ? LIST_PROPERTY_SUM : LIST_PROPERTY_UNIQUES;
      this.updateStagedItem(stagedItem, stagedItem.stagedItemType);
    }
  }

  navigateToBehavior(behavior: Behavior) {
    const behaviorItem = getMenuItemForBehavior(behavior);
    const type = isMultiAttributionBehavior(behavior)
      ? StagedItemType.BehaviorMultiAttribution
      : StagedItemType.BehaviorWithEvent;
    if (behaviorItem.stagedItemType) {
      this.updateStagedItem(behaviorItem, type);
    }
  }

  /**
   * This allows us to set the open time in an exhaustive-yet-safe manner.
   * We cannot rely on one single code path to signal a menu's first open.
   *  1) attributeCallback triggers are unordered
   *  2) It is unknown if a menu will be open on connectedCallback
   */
  private setOpenedAt() {
    // setOpenedAt may be called before this.attr managed state is available.
    if (this.isAttributeEnabled(`open`)) {
      if (!this._openedAt) {
        // Only track first menu open time
        this._openedAt = getNow();
        this._openCount += 1;

        let openedComponent: OpenedMenuComponent;
        captureWorkflowTiming(this, {
          name: `Properties Menu`,
          key: `properties-menu`,
          isComplete: (ev) => {
            openedComponent = getOpenedMenuComponent(ev, this);
            return Boolean(openedComponent);
          },
          additionalProps: () => ({
            isBetaMenu: this.isBetaMenu(),
            openCount: this._openCount,
            openedComponent,
          }),
        });
        captureWorkflowTiming(this, {
          name: `Properties Menu Loaded`,
          key: `properties-menu-loaded`,
          isComplete: (ev) => getOpenedMenuComponent(ev, this) === OpenedMenuComponent.ItemsMenu,
          additionalProps: () => ({
            isBetaMenu: this.isBetaMenu(),
            numTotalItems: this._numTotalItemsOnPrevRender,
            openCount: this._openCount,
          }),
        });
      }
    } else {
      // unset time when menu closed
      this._openedAt = null;
    }
  }

  public update(props?) {
    // Setting keydownProxyEl in state causes a re-render when opening existing property menu. We can reuse menu
    // sections in this case rather than recomputing it.
    const nonCacheInvalidatingKeys = new Set([`keydownProxyEl`]);
    const useCachedSections = isObject(props) && Object.keys(props).every((key) => nonCacheInvalidatingKeys.has(key));
    if (!useCachedSections) {
      this._cachedSections = null;
      this._cachedEventProperties = null;
      this._recentlyUsedUniqueKeys = null;
    }
    super.update(props);
  }

  public attributeChangedCallback(attr, oldVal, newVal) {
    // set open before any other code executes.
    this.setOpenedAt();

    // Calls ChildMenu::attributeChangedCallback
    super.attributeChangedCallback(attr, oldVal, newVal);

    // only update properties if project-id has already been defined
    if (!this.initialized || !this.attr(`project-id`)) {
      return;
    }

    switch (attr) {
      case `all-data-groups`:
      case `data-group-id`:
      case `event-names`:
      case `include-custom-properties`:
      case `project-has-cohorts`:
      case `hide-events`:
      case `project-has-people`:
      case `project-id`:
      case `property-combine-mode`:
      case `single-data-group`:
        // Ensure `this.updateFromSelection()` is called before `fetchCohorts()` (which is also called in `fetchData()`)
        // because fetching cohorts can trigger item replacements by calling `replaceItem()`
        // which can misbehave if this.updateFromSelection() hasn't updated `this.state.selectedFilter` from its default value.
        //
        // It misbehaves because `replaceItem()` does different things depending on whether `this.helpers.useMultipleSelection()` is true,
        // and `this.helpers.useMultipleSelection()` depends on `this.state.selectedFilter`.
        this.updateFromSelection();
        this.fetchData();
        break;
      case `selected`:
        // Ensure `this.updateFromSelection()` is called before the cohort store is updated (done in `loadStoreValues()`)
        // because fetching cohorts can trigger item replacements by calling `replaceItem()`
        // which can misbehave if this.updateFromSelection() hasn't updated `this.state.selectedFilter` from its default value.
        //
        // It misbehaves because `replaceItem()` does different things depending on whether `this.helpers.useMultipleSelection()` is true,
        // and `this.helpers.useMultipleSelection()` depends on `this.state.selectedFilter`.
        this.update({
          selected: this.attr(`selected`),
        });
        this.updateFromSelection();
        this.loadStoreValues();
        break;
      case `open`:
        this.resetStage();
        if (this.attr(`open`)) {
          this.trackEvent(TrackingEvent.OpenPropertiesDropDown);
          this.update({
            sortByValue: this.config.defaultState.sortByValue,
          });
          this.maybeNavigateToSelected();
        } else {
          this.update({
            searchFilter: ``,
          });
        }
        break;
      case `search-filter`:
        this.update({searchFilter: newVal});
        break;
      default:
        break;
    }

    // TODO don't call this on every attribute update (e.g. not on `search-filter` when user types a new character)
    this.customPropertiesStoreCallback();
  }

  public getSelectedTab() {
    return this.state.selectedTab;
  }

  public isDatetimeSelection() {
    return this.getStagedItemType() === StagedItemType.Datetime;
  }

  public isTypecastSelection() {
    return this.getStagedItemType() === StagedItemType.Typecast;
  }

  public isCohortSelection() {
    return (
      this.state.selectedTab === ResourceType.Cohort || this.state.selectedFilter?.resourceType === ResourceType.Cohort
    );
  }

  public getStagedItemType(): StagedItemType {
    const {stagedItemType} = this.state;
    if (!this.hasStagedItem() && stagedItemType) {
      // Ensure there a stack before returning type as stagedItem and stagedItemType can get out of sync.
      // Out use of stagedItemType will be refactored out; Until then, warn on any mismatch.
      reporter.error(`StagedItemType<${stagedItemType}> exists when there is not StagedItemStack`);
      return null;
    }
    return stagedItemType;
  }

  public getPrefixLabel() {
    const selected = this.getSelectedProperty();
    if (selected && `behavior` in selected) {
      return getPrefixForBehavior(selected.behavior);
    } else if (!Array.isArray(selected) && selected?.resourceType !== ResourceType.Cohort) {
      return getPropertyPathPrefix(selected, Object.values(dataGroupStore.value), {
        includeResourceLabel: Boolean(this.params.listProperty) === false && queryBuilderUtils.isCoreReport(),
      });
    }

    return null;
  }

  public getSelectedProperty(): Selection {
    return this.state.selected;
  }

  public selectedLabel() {
    const selected = this.attr(`selected`);
    const selectedItem = Array.isArray(selected) ? selected[0] : selected;
    const dataGroupId = isProperty(selectedItem) ? selectedItem.dataGroupId : null;
    return (
      getSelectedPropertyLabel({
        selected,
        cohortsStore,
        showSpecialDatePropertyUnit: this.attr(`show-special-date-property-unit`),
        dataGroupId,
        dataGroups: Object.values(this.state.dataGroups),
        expandListProperties: false,
      }) || `Select a property`
    );
  }

  public selectedLeftIcon() {
    const selected = this.state.selected || [];
    const item = Array.isArray(selected) ? selected[0] : selected;
    const resourceType = item?.resourceType;

    switch (resourceType) {
      case ResourceType.User: {
        return isProperty(item) && item.dataGroupId ? `group-attribute` : `profile-attribute`;
      }
      case ResourceType.Event:
        return `event-attribute`;
      case ResourceType.Cohort:
        return `community`;
      default: {
        // special prop icons
        if (item?.value === TOP_PEOPLE.value) {
          return TOP_PEOPLE.icon;
        }
        return null;
      }
    }
  }

  public getPersistenceKey() {
    return `properties-menu`;
  }

  private getDefaultToggleTab() {
    return this.isBetaMenu() ? this.getToggleTabs()[0].value : AllTab;
  }

  private async clickEditCustomProperty(property) {
    const {appliedValue, deletedId} = await this.promptCustomPropertiesModal(property);
    if (appliedValue) {
      this.helpers.applyCustomProperty(appliedValue, property.customPropertyId);
      this.trackEvent(TrackingEvent.EditCustomProperty, {
        context: this._context,
        name: property.name,
        resourceType: property.resourceType,
      });
    }
    if (deletedId) {
      let selected = this.state.selected;
      if (!selected) {
        selected = [];
      } else if (!Array.isArray(selected)) {
        selected = [selected];
      }
      const index = selected.findIndex(
        (selectedItem) => isProperty(selectedItem) && selectedItem.customPropertyId?.toString() === deletedId,
      );
      this.removeItem(index);
    }
  }

  private async handleItemPillClick(item) {
    if (item.pillAction === PropertyItemAction.Typecast) {
      const action = this.attr(`allow-typecast-pill-action`);
      switch (action) {
        case TypecastPillAction.Typecast:
          this.updateStagedItem(item, StagedItemType.Typecast);
          break;
        case TypecastPillAction.TriggerTutorial:
          this.selectItem(item, {commit: true});
          await chameleonReadyPromise();
          showTour(TourId.TypecastInOverflow);
          break;
        default:
          assertUnreachable(action);
      }
    } else {
      console.warn(`Invalid primary action for property item`);
    }
  }

  private updateStagedItem(stagedItem: PropertySectionItem, stagedItemType: StagedItemType) {
    const {stagedItemsStack: stagedItemsStack, stagedItemType: currentStagedItemType} = this.state;
    let updatedStack = [];
    let updatedStagedItemType = stagedItemType;
    if (stagedItem === null) {
      // Immutable pop
      updatedStack = [...stagedItemsStack].slice(0, -1);
      const itemToBeStaged: PropertySectionItem = updatedStack[updatedStack.length - 1];
      // TODO: This logic isn't very clear. The idea is if we're unstaging the leaf prop,
      // we need to update the current stagedItemType. In Shawn's logic, we just use whatever
      // the existing menuType is but generically we need to use the actual menuType of the new staged menu

      if (updatedStack.length > 0) {
        updatedStagedItemType = isNil(itemToBeStaged?.stagedItemType)
          ? currentStagedItemType
          : itemToBeStaged.stagedItemType;
      } else {
        updatedStagedItemType = null;
      }
    } else if (stagedItem?.path?.length) {
      updatedStack = [...stagedItem.path, stagedItem];
    } else {
      updatedStack = [...stagedItemsStack, stagedItem];
    }

    this.update({
      stagedItemsStack: updatedStack,
      stagedItemType: updatedStagedItemType,
      searchFilter: ``,
    });

    // If we staged an event we need to fetch props for the event
    if (stagedItemType === StagedItemType.EventWithProperties) {
      // TODO should we include dimension properties?
      // TODO load state management
      const includeDimensionProperties = !this.attr(`exclude-dimension-properties`);
      this.update({loadingEventProperties: true});
      fetchPropertyDefinitionsForEvents([stagedItem.value.value], {includeDimensionProperties}).then(() =>
        this.eventPropertiesDefinitionsStoreCallback(),
      );
    }

    this.dispatchEvent(
      new CustomEvent(`stagedproperty`, {
        detail: {
          item: updatedStack[updatedStack.length - 1] || null,
          stack: updatedStack,
        },
      }),
    );

    requestAnimationFrame(() => {
      // set browser focus to search input
      this.state.keydownProxyEl?.focus();
    });
  }

  showCreateItemModal(value: SpecialActions) {
    switch (value) {
      case SpecialActions.CreateCustomEventFormula:
        return this.createCustomProperty(ResourceType.Event, false);
      case SpecialActions.CreateCustomPeopleFormula:
        return this.createCustomProperty(ResourceType.User, false);
      case SpecialActions.CreateCustomBehavioralProperties:
        return this.createCustomProperty(ResourceType.Event, true);
      case SpecialActions.CreateInlineCohort:
        return this.createInlineCohort();
      default:
        assertUnreachable(value);
    }
  }

  handleExpandItemClick({detail: {section, sectionIdx}}: ExpanderItemClickEvent) {
    if (section.label?.includes(`Hidden`)) {
      this.trackEvent(TrackingEvent.ShowHiddenEntity);
    } else {
      if (this.isBetaMenu()) {
        this.trackEvent(TrackingEvent.ExpandedSection, {
          context: this._context,
          'Number of items in section': section.items.length,
          'Search - filter': this.state.searchFilter,
          'Section index': sectionIdx,
          'Selected tab': this.state.selectedTab,
        });
      }
    }
  }

  private handleCohortUpdate(cohortStagedValue, cohortSavedValue) {
    if (cohortStagedValue || cohortSavedValue) {
      this.commitCohort(cohortStagedValue ?? cohortSavedValue);
    }
    this.onModalClose();
  }

  private promptCustomPropertiesModal(property, forBehavioralProperty = false) {
    const attrs = {
      'default-name': this.helpers.getDefaultFormulaName(),
      'behavioral-properties-mode': forBehavioralProperty,
      'event-names': this.attr(`event-names`),
      'enable-workspace-filter-restrictions': this.attr(`enable-workspace-filter-restrictions`),
      open: true,
      'project-id': this.attr(`project-id`),
      'user-id': this.attr(`user-id`),
      property: JSON.stringify(property),
      'track-name': this.attr(`track-name`),
      'workspace-id': this.attr(`workspace-id`),
    };
    this.onModalOpen();
    return promptCustomProperties(attrs);
  }

  private promptInlineCohortsModal(cohort: CohortModel) {
    const attrs = {
      'can-save': this.attr(`project-has-cohorts`),
      'can-edit': isAllowedSuperadminOverride(cohort) || cohort.can_update_basic,
      'data-group-id': this.attr(`data-group-id`),
      elref: `inlineCohortsModal`,
      'is-new-cohort-definition': !cohort?.id,
      'can-apply-unsaved-cohort': !cohort?.id,
      name: cohort?.name,
      open: true,
      'project-has-cohorts': this.attr(`project-has-cohorts`),
      'support-b2b-cohorts': this.attr(`support-b2b-cohorts`),
      'project-id': this.attr(`project-id`),
      cohort: cohort?.serialize(),
      'track-name': this.attr(`track-name`),
      type: this.attr(`cohort-operation-type`),
      'user-id': this.attr(`user-id`),
      'workspace-id': this.attr(`workspace-id`),
      'show-cohort-count': true,
    };
    this.onModalOpen();
    return promptInlineCohorts(attrs);
  }

  private addSelectedItemToRecent(item) {
    if (this.isInlineCohort(item) || (item.customProperty && !isPersistedCustomProperty(item.customPropertyId))) {
      return;
    }

    this.addRecentItem(item.resourceType, pick(item, PropertyItemPersistedIdentifierKeys));
  }

  private selectItem(
    item: EventSectionItem | PropertySectionItem,
    {
      // Was an item in the properties menu clicked?
      onItemClicked = false,
      // Indicates whether the item selection is user triggered or automatically triggered (e.g.
      // custom properties / cohorts store callback). Note that this is not just the inverse of
      // onItemClicked. For example, if a user is creating a custom property via. the modal
      // autoTriggered will be false, and onItemClicked will also be false (since an item wasn't
      // clicked in the menu)
      autoTriggered = false,
      commit = false,
      searchFilter,
      labelMatch,
      subLabelMatch,
      customMatch,
      menuItemType,
    }: {
      onItemClicked?: boolean;
      autoTriggered?: boolean;
      commit?: boolean;
      searchFilter?: string;
      labelMatch?: SearchMatchResult;
      subLabelMatch?: SearchMatchResult;
      customMatch?: SearchMatchResult;
      menuItemType?: MenuItemType;
    } = {},
  ) {
    const propertySectionItem = item as PropertySectionItem;
    const isDimensionProperty = isProperty(propertySectionItem) && propertySectionItem.type === PropertyType.Dimension;
    if (autoTriggered && isDimensionProperty && !this.attr(`exclude-lookup-table-properties`)) {
      return;
    }

    const selectedTab = this.state.selectedTab;

    // filterToBookmarkableEventItem
    let bookmarkableSelection;
    if (menuItemType === MenuItemType.Events) {
      // events-menu + properties-menu are mixing types for events. Force the property type until these are consolidated
      bookmarkableSelection = filterToBookmarkableEventItem(item as EventItem);

      this.addRecentItem(ResourceType.Event, item);
    } else {
      bookmarkableSelection = filterToBookmarkablePropertyItem(
        pickBy(item, (value) => value !== undefined) as PropertySectionItem,
      );
      this.addSelectedItemToRecent(bookmarkableSelection);
    }

    if (this.helpers.useMultipleSelection()) {
      // if item was not removed, add it to the selection
      const baseSelection = this.getArraySelection();
      const selectionArray = baseSelection.filter((selectedItem) =>
        selectedItem.value &&
        isInlineCohort(selectedItem) &&
        isSerializedInlineCohort(selectedItem.value.cohort) &&
        isSerializedInlineCohort(item.value.cohort)
          ? !isEqual(selectedItem.value.cohort.raw_cohort, item.value.cohort.raw_cohort)
          : selectedItem.value !== item.value,
      );
      if (baseSelection.length === selectionArray.length) {
        selectionArray.push(bookmarkableSelection);
      }
      this.update({
        selected: selectionArray,
      });
    } else {
      this.dispatchSelectEvent({
        item: bookmarkableSelection,
        rawItem: propertySectionItem,
        onItemClicked,
        autoTriggered,
        commit,
        menuItemType,
        stagedItemsStack: this.state.stagedItemsStack,
      });
    }

    if (onItemClicked) {
      const propertySectionItem = item as PropertySectionItem;
      this.trackSelection(
        {
          eventName: TrackingEvent.SelectProperty,
          props: {
            'item type': menuItemType,
            'Selected label': item.label,
            'Selected tab': selectedTab,
            'Selected value': item.value,
            'Selected resource type': propertySectionItem.resourceType,
            'is custom property': isProperty(propertySectionItem) && !!propertySectionItem.customProperty,
            'is hidden entity': !!propertySectionItem.hidden,
            'is recently viewed': !!propertySectionItem.isRecentlyUsed,
            propertyDefaultType: isProperty(propertySectionItem) && propertySectionItem.propertyDefaultType,
            propertyType: propertySectionItem.propertyType,
            'Search - filter': searchFilter,
            'Search - label match': labelMatch,
            'Search - sublabel match': subLabelMatch,
            'Search - custom match': customMatch,
            'Staged items': this.state.stagedItemsStack.map((item) => item.label),
            'Elapsed time (ms) since menu opened': getNow() - this._openedAt,
            unit: propertySectionItem.unit,
          },
        },
        false, // Making this not cancelable so it's always tracked. https://mixpanel.slack.com/archives/C0KN5EVB8/p1604641156158700
      );
    }
  }

  private replaceItem(
    item,
    index,
    {
      onItemClicked = false,
      commit = false,
      autoTriggered = false,
    }: {onItemClicked?: boolean; commit?: boolean; autoTriggered?: boolean} = {},
  ) {
    // Pickout undefined values to prevent duplicate actions triggering
    const selection = filterToBookmarkablePropertyItem(
      pickBy(item, (value) => value !== undefined) as PropertySectionItem,
    );

    this.addSelectedItemToRecent(selection);

    let newSelection = selection;

    if (this.helpers.useMultipleSelection()) {
      // if item was not removed, add it to the selection
      newSelection = replaceByIndex(this.getArraySelection(), index, selection);

      this.update({
        selected: newSelection,
      });
    }

    this.dispatchSelectEvent({item: newSelection, rawItem: item, commit, autoTriggered});

    if (onItemClicked) {
      this.trackSelection({
        eventName: TrackingEvent.SelectProperty,
        props: {},
      });
    }
  }

  private removeItem(index) {
    if (this.helpers.useMultipleSelection()) {
      const selection = [...this.getArraySelection()];
      selection.splice(index, 1);

      this.update({
        selected: selection,
      });
    }
  }

  /**
   * Updates the SectionItem object with necessary information from the stagedItems stack.
   * TODO: Ideas to make this a bit cleaner
   *   TODO- This method currently mutates the item. While there are no current known issues, we should be more protective.
   *   TODO- Look into adding this information when and item being staged versus when it is being selected.
   */
  private applyContextFromStagedItems(item) {
    // TODO add typing (this should be PropertySectionItem)
    const {stagedItemsStack} = this.state;
    const stagedItemType = this.getStagedItemType();

    const [rootStagedItem, ...stagedItems] = stagedItemsStack.filter(
      (item) => isProperty(item) && !isNil(item.type), // filter out non-property items
    );
    const stagedItem = this.peekStagedItem();

    let itemValue = item;
    // TODO: this logic seems brittle. We should reconsider this design.
    if (
      ![
        StagedItemType.ComputedListProperty,
        StagedItemType.ComputedListNumericProperty,
        StagedItemType.BehaviorWithEvent,
        StagedItemType.BehaviorMultiAttribution,
        StagedItemType.EventWithProperties,
        StagedItemType.Object,
        StagedItemType.User,
        StagedItemType.Group,
        StagedItemType.Cohort,
        StagedItemType.BrowseProperties,
      ].includes(stagedItemType)
    ) {
      this.resetStage();
    }

    let baseObjectProps: TODO = null;
    if (isProperty(rootStagedItem) && rootStagedItem.type === PropertyType.Object && stagedItems.length) {
      baseObjectProps = {
        ...rootStagedItem,
        propertyObjectKey: stagedItems.map((property) => property.value),
        joinPropertyType: rootStagedItem.type,
        propertyDefaultType: isProperty(stagedItem) && stagedItem.type,
        type: stagedItem.type,
      };
    }

    if (stagedItemType === StagedItemType.Typecast) {
      itemValue = {
        ...(baseObjectProps ?? stagedItem),
        // for custom property menu, staged item is typecast because we do not show date unit selector
        // so we pass in the unit from handleClickItem directly
        ...(item.unit ? {unit: item.unit} : {}),
        type: item.value,
      };
    } else if (stagedItemType === StagedItemType.Datetime) {
      itemValue = {
        ...(baseObjectProps ?? stagedItem),
        unit: item.value,
      };
    } else if (stagedItemType === StagedItemType.Dimension) {
      itemValue = {
        ...omit(stagedItem, `dataGroupId`),
        label: stagedItem.label,
        value: item.value,
        type: item.type,
        propertyDefaultType: item.type,
        joinPropertyType: isProperty(stagedItem) && stagedItem.joinPropertyType,
        ...(stagedItem.dimensionDataGroupId && Array.isArray(item.value)
          ? {dataGroupId: stagedItem.dimensionDataGroupId}
          : {}),
        ...(isProperty(stagedItem) && stagedItem.tempDataGroupId && Array.isArray(item.value)
          ? {tempDataGroupId: stagedItem.tempDataGroupId}
          : {tempDataGroupId: undefined}),
      };
    } else if (stagedItemType === StagedItemType.Object && item.type !== PropertyType.Object) {
      this.resetStage();
      itemValue = {
        ...rootStagedItem,
        type: item.type,
        propertyObjectKey: [...stagedItems, item].map((property) => property.value),
        joinPropertyType: PropertyType.Object,
        propertyDefaultType: item.type,
      };
    } else if (
      [StagedItemType.ComputedListProperty, StagedItemType.ComputedListNumericProperty].includes(stagedItemType)
    ) {
      this.resetStage();
      itemValue = {
        ...pick(rootStagedItem, `type`, `propertyDefaultType`, `value`, `menuItemType`, `propertyDefaultType`),
        resourceType: this.params.listProperty.resourceType,
        propertyObjectKey: [...stagedItems, item].map((property) => property.value),
      };
    }
    return itemValue;
  }

  private async fetchData() {
    this.update({isLoading: true});
    await Promise.all([
      this.fetchEventProperties(),
      this.fetchUserProperties(),
      this.fetchLookupTables(),
      this.loadDataGroups(),
      this.fetchCohorts(),
      this.fetchUserDetails(),
    ]);
    this.update({isLoading: false});
  }

  private getPropertyResourceTypes() {
    return compact([
      this.canAccessUserProperties() ? ResourceType.User : null,
      this.shouldShowEventProps() ? ResourceType.Event : null,
    ]);
  }

  userPropertiesDefinitionsStoreCallback = () => {
    this.update({loadingPeopleProperties: false});
    this.getTooltipData.cache.clear();
  };

  private async fetchUserProperties() {
    if (this.canAccessUserProperties()) {
      peoplePropertyDefinitionsStore.unsubscribeAll(this.userPropertiesDefinitionsStoreCallback);
      this.update({loadingPeopleProperties: true});
      await fetchPropertyDefinitionsForUserProfiles({
        includeCustom: this.attr(`include-custom-properties`) !== IncludeCustomPropertyType.None,
        callback: this.userPropertiesDefinitionsStoreCallback,
      });
    }
  }

  private getTooltipData = memoize(this._getTooltipData, (i, r, o) => stableStringify({i, r, o}));
  private _getTooltipData(
    item: PropertySectionItem | Cohort | Selection,
    resourceType: ResourceType,
    {fromSelected = false} = {},
  ): ContextPanelData {
    const allowDescriptionEdit = !this.attr(`description-edit-disabled`);

    let tooltipData = null;
    if (resourceType === ResourceType.Cohort) {
      tooltipData = getTooltipDataForCohort(item as Cohort, {allowDescriptionEdit});
    } else {
      const propertyDefinitions = getPropertyDefinitions(this.getPropertyResourceTypes());
      const definitions = propertyDefinitions[resourceType];
      const allowDescriptionEdit = !this.attr(`description-edit-disabled`) && this.canWriteDataDefinition();
      if (Object.prototype.hasOwnProperty.call(propertyDefinitions, resourceType)) {
        let tooltipArgs;
        if (fromSelected) {
          const selectedProperty = item as Selection;
          assert(!Array.isArray(selectedProperty) && isProperty(selectedProperty));
          const dataGroupId = selectedProperty.dataGroupId || selectedProperty.tempDataGroupId;
          const dataGroupName = this.state.dataGroups[dataGroupId]?.displayName;
          tooltipArgs = {
            name: getTooltipNameForPropertyItem(selectedProperty),
            path: getTooltipPathForPropertyItem(selectedProperty, this.state.dataGroups[dataGroupId]),
            resourceType: selectedProperty.resourceType,
            dataType: selectedProperty.type,
            dataGroupId,
            dataGroupName,
            customPropertyId: selectedProperty.customPropertyId,
            isLookupTableProperty: !!selectedProperty.joinPropertyType && !!dataGroupId,
            isRecentlyUsed: this.isPropertyRecentlyUsed(selectedProperty),
          };
        } else {
          const propertySectionItem = item as PropertySectionItem;
          assert(isProperty(propertySectionItem));
          const {dataGroupId} = propertySectionItem;
          const dataGroupName = this.state.dataGroups[dataGroupId]?.displayName;
          tooltipArgs = {
            name: getTooltipNameForPropertyItem(propertySectionItem),
            path: getTooltipPathForPropertyItem(propertySectionItem, this.state.dataGroups[dataGroupId]),
            resourceType: propertySectionItem.resourceType,
            dataType: propertySectionItem.type,
            dataGroupId,
            dataGroupName,
            customPropertyId: propertySectionItem.customPropertyId,
            isLookupTableProperty: propertySectionItem.isLookupTableProperty,
            isRecentlyUsed: this.isPropertyRecentlyUsed(propertySectionItem),
          };
        }

        return getTooltipDataForProperty(tooltipArgs, definitions, {
          allowEdit: !this.attr(`hide-edit-controls`) && !this.attr(`readonly`),
          allowDescriptionEdit,
          lookupTables: lookupTablesStore.value,
          iconReferenceType: this.isBetaMenu() ? `dataType` : `resourceType`,
        });
      }
    }
    return tooltipData;
  }

  private getSelectedMenuItem(): PropertySectionItem {
    const selected = this.getSelectedItem();
    return (isProperty(selected) && selected.behavior
      ? getMenuItemForBehavior(
          selected.behavior,
          false,
          this.attr(`data-group-id`),
          Object.values(this.state.dataGroups),
        )
      : selected) as PropertySectionItem;
  }

  private getSelectedItem(): Item {
    const selected = this.state.selected;
    return Array.isArray(selected) ? selected[0] : selected;
  }

  private getSelectedResourceType(): ResourceType {
    const selectedItem = this.getSelectedItem();
    if (!selectedItem) {
      return null;
    }
    // TODO: Cohort query entries in Insights are missing resourceType pending
    // https://github.com/mixpanel/analytics/pull/27358, so assume Cohort in this case for now.
    // Remove this when not needed anymore.
    return selectedItem.resourceType || ResourceType.Cohort;
  }

  public getSelectedHoverTooltipData(): ContextPanelData {
    const selected = this.state.selected;
    if (!selected || (Array.isArray(selected) && selected.length > 1)) {
      return null;
    }

    const selectedItem = this.getSelectedItem();
    const resourceType = this.getSelectedResourceType();
    const selectedMenuItem = this.getSelectedMenuItem();
    const value = selectedMenuItem.value;
    const selectedItemTooltipData =
      !Array.isArray(selected) && !Array.isArray(value) && isProperty(selected) && selectedMenuItem.tooltipData;

    if (selectedItemTooltipData) {
      return selectedItemTooltipData;
    } else if (isProperty(selectedItem)) {
      const properties = resourceType === ResourceType.Event ? this.getEventProperties() : this.getUserProperties();
      const property = properties.find((p) => p.name === value);
      if (property && this.isPropertyDisabled(property, value)) {
        return null;
      }
      return this.getTooltipData(selectedItem, resourceType, {fromSelected: true});
    } else if (isCohort(selectedItem)) {
      const cohortId = isSavedCohort(selectedItem) ? value : value.cohort.raw_cohort.unsavedId;
      const cohortItem = this.transformCohortToCohortItem(cohortsStore.value[cohortId]);
      return this.getTooltipData(cohortItem, resourceType);
    }

    return null;
  }

  public getSelectedTrackingProps(): Obj {
    const selectedItem = this.getSelectedItem();
    if (!selectedItem) {
      return null;
    }

    const props = {};
    const resourceType = this.getSelectedResourceType();
    if (isCohort(selectedItem)) {
      props[`Entity type`] = `cohort`;
      if (selectedItem.value && !Number.isInteger(selectedItem.value)) {
        props[`Is inline`] = true;
      }
    } else {
      props[`Entity type`] = `property`;
      props[`Resource type`] = resourceType;
      if (selectedItem.customPropertyId) {
        props[`Is custom`] = true;
        if (!isPersistedCustomProperty(selectedItem.customPropertyId)) {
          props[`Is inline`] = true;
        }
      }
    }
    return props;
  }

  isInlineCohort(item: PropertySectionItem) {
    return Boolean(item.value?.cohort?.raw_cohort);
  }

  handleTooltipContextAction(ev): TooltipContextActionResult {
    const {name, item} = ev.detail;
    const isCustomProperty = name === `editCustomProperty`;
    const isCohortOnFeatureFlag = name === `editCohort`;
    const isInlineCohort = this.isInlineCohort(item);

    if (isCustomProperty || isInlineCohort || isCohortOnFeatureFlag) {
      if (isCustomProperty) {
        this.clickEditCustomProperty(item.property);
      } else {
        this.editCohort(item);
      }

      ev.stopPropagation();
      return {handled: true, openedModal: true};
    } else if ([`shareCohort`, `shareCustomProperty`].includes(name)) {
      this.handleShareTooltipContextAction(ev);
    }
    return null;
  }

  handleShareTooltipContextAction(ev) {
    const {name, item} = ev.detail;
    const isShareCohort = name === `shareCohort`;

    const {eventName, props} = shareTrack.shareButtonClick(ShareButtonUISource.ContextPanel);
    this.trackEvent(`[${shareEventPrefix}] ${eventName}`, props);
    promptSharing({
      type: isShareCohort ? SharableEntityType.Cohort : SharableEntityType.CustomProperty,
      id: isShareCohort ? item.id : item.property.customPropertyId,
      'project-id': this.attr(`project-id`),
      'is-superadmin': item.superadminOverrideShare,
    });
  }

  hasOpenedModal() {
    return false;
  }

  public getMaxRecentItemsToPersist() {
    return 15;
  }

  private getRecentlyViewedSection(
    resourceTypes: ResourceType[],
    sections: PropertySection[],
    limitPerKey = null,
  ): PropertySection {
    const allSectionsItems = sections.reduce((a, b) => [...a, ...(b.items || [])], []);

    // hide recently viewed section when searching
    if (this.getSearchFilter()) {
      return null;
    }

    return {
      label: `Recently viewed`,
      items: resourceTypes.reduce((items, resourceType) => {
        const recentItems = this.getRecentItems(resourceType, 4)
          .map((persistedItem) => {
            const persistedItemUniqueKey = getItemUniqueKey({...persistedItem, resourceType});
            const propertyItem = allSectionsItems.find((item) => {
              return getItemUniqueKey(item) === persistedItemUniqueKey;
            });
            if (!propertyItem || SPECIAL_PROPERTIES.has(propertyItem.value)) {
              return null;
            } else {
              return propertyItem;
            }
          })
          .filter(Boolean);

        if (limitPerKey) {
          recentItems.splice(limitPerKey);
        }

        return [...items, ...recentItems];
      }, []),
    };
  }

  /**
   * If `selected` = [`A`, `B`, `C`, `D`] and the properties menu items are just [`A`, `B`], unmapped selected properties are [`C`, `D`].
   */
  private getMenuSectionWithUnmappedSelected(sections) {
    const allSectionsItems = sections.reduce((a, b) => [...a, ...(b.items || [])], []);
    let selected = this.state.selected || [];
    selected = Array.isArray(selected) ? selected : [selected];

    const selectedFromThisTab = selected.filter((selection) => {
      const dataGroupId = isProperty(selection) && selection.dataGroupId;
      return [dataGroupId, selection.resourceType].includes(this.state.selectedTab);
    });
    const unmappedSelected = selectedFromThisTab.filter((item) => !this.isItemSelected(item.value, allSectionsItems));

    if (!unmappedSelected.length) {
      return sections;
    }
    // TODO(Gonzalo): We will use type-text icon until we have a defined way to specify the type (user, event) of the property the user selected.
    const icon = `type-text`;

    const extraItems = unmappedSelected.map((ex) => ({
      ...ex,
      isSelected: true,
      label: typeof ex.value === `string` ? ex.value : isCohort(ex) && ex.label,
      icon,
    }));
    if (sections.length === 1) {
      return [
        {
          ...sections[0],
          items: [...sections[0].items, ...extraItems],
        },
      ];
    }
    return [
      ...sections,
      {
        items: extraItems,
      },
    ];
  }

  private getMenuSectionWithRecentlyViewed(
    resourceTypes: ResourceType[],
    sections: PropertySection[],
    limitPerKey = null,
  ): PropertySection[] {
    return [
      this.getRecentlyViewedSection(resourceTypes, sections, limitPerKey),
      {
        isLoading: this.state.loadingEventProperties,
        items: this.getPropertyMenuItems({
          properties: this.getCustomFormulas(
            [this.shouldShowEventProps() && ResourceType.Event, this.shouldShowUserProps() && ResourceType.User].filter(
              Boolean,
            ),
          ),
        }),
        label: `Formulas in this report`,
        showLimit: MENU_ITEM_LIMIT,
      },
      ...sections,
    ].filter(Boolean);
  }

  private getMenuSections(): CombinedSection[] {
    if (!this._cachedSections) {
      if (this.hasStagedItem()) {
        this._cachedSections = this.getSectionsForStagedItem(this.getStagedItemType(), this.peekStagedItem());
      } else if (this.params.listProperty) {
        this._cachedSections = this.helpers.getObjectMenuSection();
      } else {
        this._cachedSections = this.getTabbedMenuSections();
      }
    }
    return this._cachedSections;
  }
  /**
   * Returns all sections to be rendered in the selection menu

   * based on the attribute configuration
   */
  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  private getTabbedMenuSections(): PropertySection[] {
    const {selectedTab} = this.state;
    const projectHasDataGroups = this.attr(`all-data-groups`);
    const projectHasEventProps = this.shouldShowEventProps();
    const projectHasEvents =
      this.attr(`menu-item-types`).includes(MenuItemType.Events) ||
      this.attr(`menu-item-types`).includes(MenuItemType.EventWithProperties);
    const hasOtherOptions = this.attr(`other-options`).length > 0;
    const showRecentlyViewed = !this.attr(`hide-recently-viewed`) && !this.isBetaMenu();

    let sections;

    switch (selectedTab) {
      case AllTab:
        sections = [
          ...(hasOtherOptions ? [this.getOtherMenuSection()] : []),
          ...(projectHasEventProps ? this.getEventsMenuSections() : []),
          ...(this.shouldShowUserProps() ? this.getUserMenuSections() : []),
          ...(projectHasDataGroups ? this.getDataGroupsSections() : []),
          ...(this.shouldShowCohorts() ? this.getCohortsMenuSections() : []),
        ];
        if (showRecentlyViewed) {
          sections = this.getMenuSectionWithRecentlyViewed(
            [ResourceType.Event, ResourceType.User, ResourceType.Other],
            sections,
            2,
          );
        }
        break;
      case AllPropertiesTab: {
        sections = this.getAllPropertiesSections();
        break;
      }
      case EventsInPropertiesMenuTab:
        sections = projectHasEvents ? this.getEventsSections() : [];
        break;
      case ResourceType.Event:
        sections = projectHasEventProps ? this.getEventsMenuSections() : [];
        if (sections.length > 0 && showRecentlyViewed) {
          sections = this.getMenuSectionWithRecentlyViewed([ResourceType.Event], sections);
        }
        break;
      case ResourceType.User:
        sections = this.shouldShowUserProps() ? this.getUserMenuSections() : [];
        if (sections.length > 0 && showRecentlyViewed) {
          sections = this.getMenuSectionWithRecentlyViewed([ResourceType.User], sections);
        }
        break;
      case ResourceType.Cohort:
        sections = this.shouldShowCohorts() ? this.getCohortsMenuSections() : [];
        break;
      default:
        sections = projectHasDataGroups ? this.getDataGroupsSections(selectedTab) : [];
    }

    if (this.helpers.useMultipleSelection()) {
      sections = this.getMenuSectionWithUnmappedSelected(sections);
    }

    if (this.state.getSortedMenuSections) {
      sections = this.state.getSortedMenuSections(sections);
    }

    if (this.hasLookupTables() && !this.getSearchFilter()) {
      // remove lookup table properties when there is no search filter (except in the "Recent" section)
      sections = this.filterLookupTablePropertiesFromSections(sections);
    }

    const eventNameKey = stableStringify(
      isQueryOnAllEvents(this.attr(`event-names`)) ? [SPECIAL_EVENTS.ALL_EVENTS] : this.attr(`event-names`),
    );
    if (!this._instanceEventLoadHistory.has(eventNameKey) && !this.state.isLoading) {
      this._instanceEventLoadHistory.add(eventNameKey);
      const menuLoadedProperties = {
        prefetchEnabled: propMenuPrefetchEnabled,
        'has queried this page load': _PAGE_EVENT_LOAD_HISTORY.has(eventNameKey),
      };
      this._trackNextRenderWithProps = menuLoadedProperties;
      // ensure this gets tracked at the end of the update cycle
      requestAnimationFrame(() => {
        this.trackEvent(TrackingEvent.MenuLoadedRAF, {
          elapsedMs: getNow() - this._openedAt,
          ...menuLoadedProperties,
        });
      });
    }
    _PAGE_EVENT_LOAD_HISTORY.add(eventNameKey);

    return sections;
  }

  /**
   * Returns a new sorted array.
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  private sortPropertiesByScore(
    properties: Array<PropertySectionItem | EventSectionItem>,
  ): Array<PropertySectionItem | EventSectionItem> {
    const searchScores = {};
    let maxSearchScore = 0;
    let maxPopularity = 0;
    properties.forEach((item) => {
      const searchScore = (this.state.searchFilter && fuzzyMatch(item.label, this.state.searchFilter)?.score) || 0;
      searchScores[getItemUniqueKey(item)] = searchScore;
      if (searchScore > maxSearchScore) {
        maxSearchScore = searchScore;
      }

      const popularity = Number(item.uiUniqueQueryCount) || 0;
      if (popularity > maxPopularity) {
        maxPopularity = popularity;
      }
    });

    const SEARCH_SCORE_WEIGHT = 3;
    const RECENTLY_USED_WEIGHT = 2;
    const EVENT_WEIGHT = -0.01; // all things being equal, rank events below properties

    const propertiesWithMeta = properties
      .map((item) => {
        const isRecentlyUsed = isEventSectionItem(item) ? item.value.isRecentlyUsed : item.isRecentlyUsed;
        const isEventProperty = isPropertySectionItem(item) && item.resourceType === ResourceType.Event;
        let recentlyUsed = isRecentlyUsed ? 1 : 0;
        const globalRelevance = (isEventProperty && isPropertySectionItem(item) && item.densityGlobal) || 0;
        let localRelevance = (isEventProperty && isPropertySectionItem(item) && item.densityLocal) || 0;
        if (isPropertySectionItem(item) && isProperty(item) && isInlineCustomProperty(item.customPropertyId)) {
          recentlyUsed = 1;
          localRelevance = 1;
        }
        const popularity = maxPopularity ? (Number(item.uiUniqueQueryCount) || 0) / maxPopularity : 0;
        const eventModifier = isEventSectionItem(item) ? EVENT_WEIGHT : 0;
        const searchScore = searchScores[getItemUniqueKey(item)];
        const normalizedSearchScore = maxSearchScore ? searchScore / maxSearchScore : 0;
        const score =
          normalizedSearchScore * SEARCH_SCORE_WEIGHT +
          recentlyUsed * RECENTLY_USED_WEIGHT +
          globalRelevance +
          localRelevance +
          popularity +
          eventModifier;

        if (isNaN(score)) {
          throw Error(`Score is NaN.`);
        }

        return {
          item,
          score,
          scoreParts: [
            normalizedSearchScore * SEARCH_SCORE_WEIGHT,
            recentlyUsed * RECENTLY_USED_WEIGHT,
            globalRelevance,
            localRelevance,
            popularity,
            eventModifier,
          ],
        };
      })
      .filter((item) => isNaN(SPECIAL_ORDER_PROPERTIES.get(item.item.value)));

    propertiesWithMeta.sort((a, b) => {
      return b.score - a.score;
    });

    if (currentProjectStore.value?.hasFeatureFlag(ProjectFeatureFlag.Debug)) {
      propertiesWithMeta.forEach((entry) => {
        const meta = {...entry};
        delete meta.item;
        entry.item.subLabel = JSON.stringify(meta);
      });
    }

    let sortedProperties = propertiesWithMeta.map((entry) => entry.item);

    // insert special order properties at the correct spot (temporary until we work on new prop menu)
    properties
      .filter((item) => !isNaN(SPECIAL_ORDER_PROPERTIES.get(item.value)))
      .forEach((item) => {
        const order = SPECIAL_ORDER_PROPERTIES.get(item.value);
        sortedProperties = [...sortedProperties.slice(0, order), item, ...sortedProperties.slice(order)];
      });

    return sortedProperties;
  }

  private getEventsSections(
    stagedItem: PropertySectionItem = null,
    type: MenuItemType.Events | MenuItemType.EventWithProperties = MenuItemType.Events,
    selected: PropertySectionItem = null,
  ): EventSection[] {
    const eventsSections = this.getContext(`eventsController`).getSections({
      includeTags: false,
      dataGroupId: this.attr(`data-group-id`),
      selected,
      showAnyEvent: true,
      showSessionEvent: true,
      mainSectionShowLimit: MENU_ITEM_LIMIT,
      sortByValue: this.canSortItems() ? this.state.sortByValue : null,
      recentlyUsedEvents: this.getRecentItems(ResourceType.Event),
      isBetaMenu: this.isBetaMenu(),
      subLabel:
        this.isBetaMenu() && this.state.selectedFilter?.value === `all` && !stagedItem
          ? `Event${PATH_SEPARATOR}`
          : null,
      mainSectionLabel: `Did Event`,
    }) as Array<Section<EventItem>>;

    return [
      ...eventsSections.map((section) => ({
        ...section,
        type,
        items: section.items.map((item) => ({...item, menuItemType: type})),
      })),
    ];
  }

  private getAllEventProperties(eventNames: string[]) {
    let eventProperties = [];
    if (this.shouldShowEventProps() && !this.state.loadingEventProperties) {
      const includeDimensionProperties = !this.attr(`exclude-dimension-properties`);
      if (isQueryOnAllEvents(eventNames)) {
        eventProperties = getAllGlobalPropertyDefinitions();
      } else if (this.shouldFilterToEventPropertyIntersection()) {
        const eventPropertiesResults = eventNames.map((eventName) =>
          getAllPropertyDefinitionsForEvents([eventName], {includeDimensionProperties}),
        );
        // @ts-ignore
        eventProperties = calculatePropertiesIntersection(eventPropertiesResults);
      } else {
        eventProperties = getAllPropertyDefinitionsForEvents(eventNames, {includeDimensionProperties});
      }

      if (this.attr(`scd-poc-enabled`)) {
        // Get SCD properties and add extra metadata to them
        const scdProperties = getAllPropertyDefinitionsForEvents([SCD_EVENT_NAME], {includeDimensionProperties}).map(
          (property) => {
            property.isScd = true;
            return property;
          },
        );
        // Merge them
        eventProperties = eventProperties.concat(scdProperties);
      }
    } else {
      return [];
    }

    eventProperties = eventProperties.map((prop) => getDataDefinitionsPropertyMenuItem(prop));

    eventProperties = this.addTempMappingsToProperties(ResourceType.Event, false, eventProperties);

    return eventProperties;
  }

  private handleSearchInput(searchFilter) {
    const isStartingSearch = Boolean(searchFilter && !this.state.searchFilter);
    const isClearingSearch = Boolean(this.state.searchFilter && !searchFilter);
    this.update({searchFilter});
    if (isStartingSearch) {
      this.trackEvent(TRACKING_EVENTS.SEARCH_STARTED);
    } else if (isClearingSearch) {
      this.trackEvent(TRACKING_EVENTS.SEARCH_CLEARED);
    }
  }

  private debouncedSearchInput = debounce((searchInput) => this.handleSearchInput(searchInput), 200);

  private getEventProperties({
    includeHidden = false,
    onlyCustom = false,
    eventNames = null,
  }: {includeHidden?: boolean; onlyCustom?: boolean; eventNames?: string[]} = {}) {
    if (!this._cachedEventProperties) {
      this._cachedEventProperties = this.getAllEventProperties(eventNames ?? this.eventNames());
    }

    // we filter out custom properties here to prevent double count. custom properties from data definitions store & custom properties store are stitched together in getPropertyDefinitions
    // eventually, we want to abstract the stitching to the definitions store
    let eventProperties = this._cachedEventProperties.filter((prop) =>
      onlyCustom ? prop.status === `custom` : prop.status !== `custom`,
    );

    if (!includeHidden) {
      eventProperties = eventProperties.filter((prop) => !prop.hidden);
    }
    return eventProperties;
  }

  private hasLookupTables() {
    return Object.keys(lookupTablesStore.value).length !== 0;
  }

  private expandLookupTableProperties(joinProperty: PropertySectionItem): PropertySectionItem[] {
    assert(isProperty(joinProperty));
    let lookupTableProperties: PropertySectionItem[] = [];
    const dataGroupId = joinProperty.tempDataGroupId || joinProperty.dimensionDataGroupId;
    const lookupProps: LookupTableProperty[] = lookupTablesStore.value[dataGroupId]?.properties;
    if (lookupProps) {
      const propertyDefinitionsForEventsStoreKey = this.getPropertyDefinitionsForEventsStoreKey();
      lookupTableProperties = lookupProps.map((lookupTableProp) => {
        return this.lookupTablePropToPropertySectionItem(
          joinProperty,
          lookupTableProp,
          propertyDefinitionsForEventsStoreKey,
        );
      });
    }
    return lookupTableProperties;
  }

  private lookupTablePropToPropertySectionItem(
    joinProperty: SectionItemProperty,
    lookupProperty: LookupTableProperty,
    propertyDefinitionsForEventsStoreKey: string,
  ) {
    const {
      resourceType,
      dimensionDataGroupId,
      tempDataGroupId,
      joinPropertyType,
      name: joinPropertyName,
      label: joinPropertyLabel,
      customPropertyId,
    } = joinProperty;
    const dataGroupId = tempDataGroupId || dimensionDataGroupId;
    return this.getPropertyMenuItem({
      property: {
        name: lookupProperty.name,
        label: lookupProperty.label,
        type: lookupProperty.type,
        propertyDefaultType: lookupProperty.type,
        count: lookupProperty.count,
        resourceType,
        dataGroupId,
        joinPropertyType,
        joinPropertyLabel,
        value: [joinPropertyName, lookupProperty.name],
        isLookupTableProperty: true,
        ...(tempDataGroupId ? {tempDataGroupId} : {}),
        ...(customPropertyId ? {customPropertyId} : {}),
      },
      propertyDefinitionsForEventsStoreKey,
    });
  }

  public getItemUniqueKey(item) {
    return getItemUniqueKey(item);
  }

  /**
   * Expands a list of properties with the properties defined in a lookup table for each mapped property in the list
   * this expansion is only done when there is a search term in the dropdown
   */
  private getAllLookupTablePropertiesForDimensionProps(properties: TODO): TODO {
    if (!this.hasLookupTables() || this.attr(`exclude-lookup-table-properties`)) {
      return [];
    }

    return properties
      .filter((property) => isProperty(property) && property.type === PropertyType.Dimension)
      .flatMap((property) => this.expandLookupTableProperties(property));
  }

  private filterLookupTablePropertiesFromSections(sections: PropertySection[]) {
    return sections.map((section) => {
      if ([MenuItemType.EventProperties, MenuItemType.UserProperties].includes(section.type)) {
        section.items = section.items.filter((item) => !item.isLookupTableProperty);
        return section;
      } else {
        return section;
      }
    });
  }

  private getLookupTablePropertyFromSelected(selected: Item) {
    let properties = [];
    if (selected.resourceType === ResourceType.Event) {
      properties = this.getEventProperties();
    } else if (selected.resourceType === ResourceType.User) {
      properties = this.getUserProperties();
    }
    return properties.find((prop) => {
      return prop.name === selected.value[0];
    });
  }

  private getAllPropertiesSections(): CombinedSection[] {
    const projectHasEvents = this.attr(`menu-item-types`).includes(MenuItemType.Events);
    const projectHasEventProps = this.shouldShowEventProps();
    const projectHasDataGroups = this.attr(`all-data-groups`);
    const hasOtherOptions = this.attr(`other-options`).length > 0;

    // these are functions so we don't waste cycles when they aren't ultimately needed
    // based on the switch below
    const eventsSections = () => (projectHasEvents ? [this.getEventsSections()[0]] : []);
    const eventPropSections = () => (projectHasEventProps ? this.getEventsMenuSections() : []);
    const userPropSections = () => (this.shouldShowUserProps() ? this.getUserMenuSections() : []);
    const dataGroupPropSections = () => (projectHasDataGroups ? this.getDataGroupsSections() : []);
    const cohortsSections = () => (this.shouldShowCohorts() ? this.getCohortsMenuSections() : []);

    const filter = this.state.selectedFilter;
    let sectionsOfAvailableProperties: Array<PropertySection | EventSection> = [];
    let otherOptions = [];
    switch (filter.resourceType) {
      case ResourceType.Event:
        sectionsOfAvailableProperties = eventPropSections();
        break;
      case ResourceType.User:
        if (filter.dataGroupId) {
          sectionsOfAvailableProperties = [
            dataGroupPropSections().find((section) =>
              section.items.some((item) => `dataGroupId` in item && item.dataGroupId === filter.dataGroupId),
            ),
          ];
        } else {
          sectionsOfAvailableProperties = userPropSections();
        }
        break;
      case ResourceType.Cohort: {
        const [yourCohortsSection, otherCohortsSection] = cohortsSections();
        const [yourSelectedCohorts, yourUnselectedCohorts] = partition(
          yourCohortsSection.items,
          (item) => item.isSelected,
        );
        const [otherSelectedCohorts, otherUnselectedCohorts] = partition(
          otherCohortsSection.items,
          (item) => item.isSelected,
        );
        sectionsOfAvailableProperties = [
          {
            items: [
              ...yourSelectedCohorts,
              ...otherSelectedCohorts,
              ...yourUnselectedCohorts,
              ...otherUnselectedCohorts,
            ],
          },
        ];
        break;
      }
      case `events`:
        sectionsOfAvailableProperties = eventsSections();
        break;
      default:
        otherOptions = (hasOtherOptions && this.getOtherMenuSection()?.items) || [];
        sectionsOfAvailableProperties = [
          ...eventsSections(),
          ...eventPropSections(),
          ...userPropSections(),
          ...dataGroupPropSections(),
          ...cohortsSections(),
        ];
        break;
    }
    const allItems = sectionsOfAvailableProperties.flatMap((s) => {
      const items: Array<PropertySectionItem | EventSectionItem> = s?.items ?? [];
      return items;
    });

    // cohorts are already sorted in the order we want from the switch statement above
    let sorted = [...otherOptions, ...allItems];

    if (filter.resourceType !== ResourceType.Cohort) {
      sorted = this.sortProperties(sorted);
    }
    const filtered = sorted.filter((item) => isEventSectionItem(item) || !item.hidden);

    const sections: CombinedSection[] = [
      {
        items: filtered,
        showLimit: null,
        hideTopDivider: true,
      },
    ];

    const hiddenItems = sorted.filter((item) => isPropertySectionItem(item) && item.hidden);
    if (!this.state.isLoading) {
      sections.push({
        label: `Hidden Properties`,
        items: hiddenItems,
        isHidden: true,
        hideTopDivider: true,
      });
    }
    return sections;
  }

  private getEventsMenuSections({
    item,
    hideSectionLabel,
    isForMultiAttribution,
  }: {
    item?: PropertySectionItem;
    hideSectionLabel?: boolean;
    isForMultiAttribution?: boolean;
  } = {}): PropertySection[] {
    const specialEventProperties: Array<SpecialControl | PropertySectionItem> = [];
    const eventNames = item?.value ? [item.value.value] : null;
    // Include all event properties for multi attribution property menu
    let properties: Array<PropertyDefinition | SpecialControl | PropertySectionItem> = this.getEventProperties({
      eventNames: isForMultiAttribution ? [SPECIAL_EVENTS.ANY_EVENT] : eventNames,
    });

    if (this.shouldShowEventProps() && this.attr(`include-custom-properties`) !== IncludeCustomPropertyType.None) {
      properties = [...properties, ...this.getCustomFormulas([ResourceType.Event])];
      if (!this.attr(`enable-workspace-filter-restrictions`)) {
        // workspace filters can use inline custom properties, but cannot refer to persisted custom properties
        properties = [...properties, ...this.getCustomProperties(ResourceType.Event)];
      }
    }

    if (!isForMultiAttribution) {
      if (this.canCreateCustomEventProperties() && !this.isBetaMenu()) {
        specialEventProperties.push(SPECIAL_CONTROLS.CREATE_CUSTOM_EVENT_FORMULA);
      }

      if (this.attr(`show-special-dataset-property`)) {
        specialEventProperties.push(DATASET_PROPERTY);
      }
    }

    const specialPropertyMenuItems = this.getPropertyMenuItems({properties: specialEventProperties});
    let propertyMenuItems: Array<PropertySectionItem | EventSectionItem> = this.getPropertyMenuItems({
      properties,
      resourceType: ResourceType.Event,
    });

    /*
      Removing this sort causes the following test to fail
      [Widgets:mp-query-builder:lookup tables:mapped custom properties:table properties are selectable via search]
      TODO: Research if this is an incorrect test check or if something inside propertyMenuItems is truly required
    */
    propertyMenuItems = this.sortProperties(propertyMenuItems);
    propertyMenuItems = this.prepareDimensionProperties(propertyMenuItems);

    if (this.helpers.useMultipleSelection()) {
      // the multi-select mode is mostly used by the users report and live view column pickers, where
      // we hard-code some default properties to begin with. But they might not be part of the
      // project's property set. We include those that are not so they still show up in the
      // picker and can be toggled off
      const selected = this.state.selected;
      if (Array.isArray(selected)) {
        const propertyDefinitionsForEventsStoreKey = this.getPropertyDefinitionsForEventsStoreKey();
        propertyMenuItems = [
          ...propertyMenuItems,
          ...selected
            .filter((item) => !properties.find((p) => `value` in p && p.value === item.value))
            .map((prop) =>
              this.getPropertyMenuItem({
                property: prop,
                selected: prop,
                propertyDefinitionsForEventsStoreKey,
              }),
            ),
        ];
      }
    }

    let eventPropertyItems = [
      // special properties should always be on top
      ...specialPropertyMenuItems,
      ...(this.sortProperties(propertyMenuItems) as PropertySectionItem[]),
    ];

    if (isForMultiAttribution) {
      eventPropertyItems = this.filterMultiAttributionProperties(eventPropertyItems);
    }
    const eventPropertiesSection = {
      type: MenuItemType.EventProperties,
      isLoading: this.state.loadingEventProperties,
      items: eventPropertyItems,
      label: hideSectionLabel || this.attr(`hide-section-labels`) ? null : `Event properties`,
      showLimit: 0,
    };

    return [eventPropertiesSection, ...this.getHiddenEventsMenuSections({isForMultiAttribution})];
  }

  private getHiddenEventsMenuSections({
    isForMultiAttribution,
  }: {isForMultiAttribution?: boolean} = {}): PropertySection[] {
    const hiddenProperties = this.getEventProperties({includeHidden: true}).filter((def) => def.hidden);
    let eventPropertyItems = this.getPropertyMenuItems({
      properties: hiddenProperties,
      resourceType: ResourceType.Event,
    });
    if (isForMultiAttribution) {
      eventPropertyItems = this.filterMultiAttributionProperties(eventPropertyItems);
    }
    return [
      {
        type: MenuItemType.EventProperties,
        isLoading: this.state.loadingEventProperties,
        items: eventPropertyItems,
        label: `Hidden Event Properties`,
        showLimit: MENU_ITEM_LIMIT,
        isHidden: true,
      },
    ];
  }

  private isVisibleCustomPropertyForEvents(customProperty, eventPropertiesNameSet: Set<string>): boolean {
    const eventNames = this.eventNames();

    if (isQueryOnAllEvents(eventNames)) {
      return true;
    }

    // $date is a special event property which exists on all events, but is not always returned
    // by getEventProperties
    eventPropertiesNameSet.add(MPProperties.Date);

    if (isPersistedCustomProperty(customProperty.customPropertyId)) {
      const referencedEventProperties = customProperty.referencedRawEventProperties || [];
      // persisted custom properties know from the backend response all the transitively
      // referenced raw event properties
      return (
        referencedEventProperties.length === 0 ||
        Object.values(customProperty.referencedRawEventProperties).some((property: string) =>
          eventPropertiesNameSet.has(property),
        )
      );
    } else {
      const hasUserPropertiesOnly = Object.values(customProperty.composedProperties).every(
        (prop: PropertySectionItem) => prop.resourceType === ResourceType.User,
      );
      // for non-persisted custom properties we need to compute the transitively referenced
      // raw event properties by combining the referenced properties of any nested custom events
      // and any properties referenced directly by this property
      return (
        hasUserPropertiesOnly ||
        Object.values(customProperty.composedProperties).some((composedProperty: any) => {
          if (composedProperty.customPropertyId) {
            const referencedEventProperties =
              customPropertiesStore.value[composedProperty.customPropertyId].referencedRawEventProperties;
            return (
              referencedEventProperties.length === 0 ||
              referencedEventProperties.some((property: string) => eventPropertiesNameSet.has(property))
            );
          } else {
            return (
              composedProperty.resourceType === ResourceType.Event && eventPropertiesNameSet.has(composedProperty.value)
            );
          }
        })
      );
    }
  }

  private isVisibleCustomProperty(customPropertyId: string, eventPropertiesNameSet: Set<string>): boolean {
    if (!isPersistedCustomProperty(customPropertyId)) {
      return true;
    }

    const id = isPersistedCustomProperty(customPropertyId) ? parseInt(customPropertyId) : customPropertyId;
    const customProperty = customPropertiesStore.value[id];
    const property = storeCustomPropertyToProperty(customPropertyId);

    // exclude persisted behavioral props
    if (
      this.attr(`include-custom-properties`) === IncludeCustomPropertyType.PersistedNonBehavioralOnly &&
      isBehavioralCustomProperty(customProperty)
    ) {
      return false;
    }

    if (
      property.resourceType === ResourceType.Event &&
      !this.isVisibleCustomPropertyForEvents(customProperty, eventPropertiesNameSet)
    ) {
      return false;
    }

    return customProperty.isVisible || customProperty.user === this.attr(`user-id`);
  }

  private getCustomEntities(resourceTypes: ResourceType[]) {
    const eventPropertiesNameSet = new Set<string>(
      this.getEventProperties({includeHidden: true}).map((prop) => prop.name),
    );

    // TODO @jimmy: add uiUniqueQueryCount as a field in the custom properties API
    const customDefinitions = keyBy(
      this.getEventProperties({includeHidden: false, onlyCustom: true}),
      `customPropertyId`,
    );

    const propertyDefinitionsForEventsStoreKey = this.getPropertyDefinitionsForEventsStoreKey();
    return Object.keys(customPropertiesStore.value)
      .filter((customPropertyId) => !customPropertiesStore.value[customPropertyId].newCustomPropertyId)
      .filter(
        (customPropertyId) =>
          this.attr(`include-custom-properties`) === IncludeCustomPropertyType.All ||
          isPersistedCustomProperty(customPropertyId),
      )
      .filter((customPropertyId) => this.isVisibleCustomProperty(customPropertyId, eventPropertiesNameSet))
      .map((customPropertyId) => {
        let property = storeCustomPropertyToProperty(customPropertyId);
        const propertyDef = customDefinitions[customPropertyId];

        property = {
          ...property,
          ...(propertyDef ? {uiUniqueQueryCount: propertyDef.uiUniqueQueryCount} : {}),
        };

        return this.getPropertyMenuItem({
          property,
          resourceType: property.resourceType,
          propertyDefinitionsForEventsStoreKey,
        });
      })
      .filter((item) => resourceTypes.includes(item.resourceType));
  }

  private getCustomProperties(resourceType: ResourceType) {
    let properties = this.getCustomEntities([resourceType]).filter((item) =>
      isPersistedCustomProperty(isProperty(item) && item.customPropertyId),
    );
    if (resourceType === ResourceType.Event || resourceType === ResourceType.User) {
      properties = this.addTempMappingsToProperties(resourceType, true, properties);
    }
    return properties;
  }

  private getCustomFormulas(resourceTypes: ResourceType[]) {
    return this.getCustomEntities(resourceTypes).filter(
      (item) => !isPersistedCustomProperty(isProperty(item) && item.customPropertyId),
    );
  }

  private getUserProperties({includeHidden = false}: {includeHidden?: boolean} = {}): PropertyDefinition[] {
    if (!this.shouldShowUserProps() || this.state.loadingPeopleProperties) {
      return [];
    }
    // we filter out custom properties here to prevent double count. custom properties from data definitions store & custom properties store are stitched together in getPropertyDefinitions
    // eventually, we want to abstract the stitching to the definitions store
    let userProperties = getAllPropertyDefinitionsForUserProfiles()
      .filter((prop) => prop.status !== `custom`)
      .map((prop) => getDataDefinitionsPropertyMenuItem(prop));

    if (!includeHidden) {
      userProperties = userProperties.filter((prop) => !prop.hidden);
    }

    userProperties = this.addTempMappingsToProperties(ResourceType.User, false, userProperties);

    return userProperties;
  }

  private isFirstEventTimeEnabled(): boolean {
    // first event time is backed by a custom behavioral property:
    // - behavioral properties are not allowed in workspace filters
    // - first event time is only valid in situations where custom properties are valid
    return (
      !this.attr(`enable-workspace-filter-restrictions`) &&
      ![IncludeCustomPropertyType.PersistedNonBehavioralOnly, IncludeCustomPropertyType.None].includes(
        this.attr(`include-custom-properties`),
      )
    );
  }

  private sortProperties(properties: Array<PropertySectionItem | EventSectionItem>) {
    if (this.canSortItems()) {
      switch (this.state.sortByValue) {
        case SortBy.Relevance:
          return this.sortPropertiesByScore(properties);
        case SortBy.NameAsc:
          return this.sortPropertiesByFullPath(properties, {order: OrderBy.Ascending});
        case SortBy.NameDesc:
          return this.sortPropertiesByFullPath(properties, {order: OrderBy.Descending});
      }
    }
    return sortItemsByLabel(properties);
  }

  private sortPropertiesByFullPath(
    properties: Array<PropertySectionItem | EventSectionItem>,
    {order}: {order: OrderBy} = {order: OrderBy.Ascending},
  ) {
    return properties.sort(
      baseComparator({
        order,
        transform: (item: PropertySectionItem) => {
          return [item.subLabel?.toLowerCase(), item.label.toLowerCase()].filter(Boolean).join(` `);
        },
      }),
    );
  }

  private isBehavioralPropertiesEnabled(): boolean {
    // behavioral properties can never be used in workspace filters
    if (this.attr(`enable-workspace-filter-restrictions`)) {
      return false;
    }
    // if the containing report allows for behavioral props they are always available
    if (this.attr(`allow-behavioral-property-creation`)) {
      return true;
    }
    // otherwise feature flag determines availability (feature flag is being phased out,
    // eventually only reports with allow-behavioral-property-creation will support them)
    return currentProjectStore.value?.hasFeatureFlag(ProjectFeatureFlag.BehavioralProperties);
  }

  private prepareDimensionProperties(properties: TODO) {
    if (this.attr(`exclude-dimension-properties`)) {
      return this.unmapDimensionProperties(properties);
    } else if (this.isBetaMenu()) {
      return [
        ...this.unmapDimensionProperties(properties),
        ...this.getAllLookupTablePropertiesForDimensionProps(properties),
      ];
    } else {
      return [...properties, ...this.getAllLookupTablePropertiesForDimensionProps(properties)];
    }
  }

  private unmapDimensionProperties(properties: TODO): TODO {
    // Make mapped properties look like they are unmapped.
    const [dimensionProperties, allOtherProperties] = partition(
      properties,
      (property) => isProperty(property) && property.type === PropertyType.Dimension,
    );
    const dimensionKeysToRemove = [
      `dimensionDataGroupId`,
      `icon`,
      `joinPropertyType`,
      `join_property_type`, // Todo: Find out how this key still exists, it should have been reformatted.
    ];
    const propertyDefinitionsForEventsStoreKey = this.getPropertyDefinitionsForEventsStoreKey();
    return [
      ...allOtherProperties,
      ...dimensionProperties.map((property) =>
        this.getPropertyMenuItem({
          property: {
            ...(omit(property, dimensionKeysToRemove) as PropertySectionItem),
            type: isProperty(property) && property.joinPropertyType,
          },
          propertyDefinitionsForEventsStoreKey,
        }),
      ),
    ];
  }

  private getUserMenuSections(): PropertySection[] {
    let properties: Array<PropertyDefinition | PropertySectionItem> = this.getUserProperties();
    properties = [
      // peoplePropertiesStore properties
      ...properties,
      // special first event time property
      ...(this.isFirstEventTimeEnabled() ? [FIRST_EVENT_TIME_PROPERTY] : []),
    ];

    if (this.attr(`include-custom-properties`) !== IncludeCustomPropertyType.None) {
      properties = [...properties, ...this.getCustomFormulas([ResourceType.User])];
      if (!this.attr(`enable-workspace-filter-restrictions`)) {
        // workspace filters can use inline custom properties, but cannot refer to persisted custom properties
        properties = [...properties, ...this.getCustomProperties(ResourceType.User)];
      }
    }

    let propertySectionItems = this.sortProperties(
      this.getPropertyMenuItems({properties, resourceType: ResourceType.User}),
    );

    if (this.canCreateCustomUserProperties() && !this.isBetaMenu()) {
      const creationItems = [
        ...(this.isBehavioralPropertiesEnabled() ? [SPECIAL_CONTROLS.CREATE_CUSTOM_BEHAVIORAL_PROPERTY] : []),
        SPECIAL_CONTROLS.CREATE_CUSTOM_PEOPLE_FORMULA,
      ];
      propertySectionItems = [...this.getPropertyMenuItems({properties: creationItems}), ...propertySectionItems];
    }
    propertySectionItems = this.prepareDimensionProperties(propertySectionItems);
    const userPropertiesSection = {
      type: MenuItemType.UserProperties,
      isLoading: this.state.loadingPeopleProperties,
      items: propertySectionItems as PropertySectionItem[],
      label: this.attr(`hide-section-labels`) ? null : `Profile properties`,
      showLimit: MENU_ITEM_LIMIT,
    };

    return [userPropertiesSection, ...this.getHiddenUserMenuSections()];
  }

  private getHiddenUserMenuSections() {
    let hiddenProperties = this.getUserProperties({includeHidden: true}).filter((def) => def.hidden);
    /*
      TODO (duc): this is a quick patch (which hide the dim prop instead of showing it as a normal prop)
      Need to make user properties re-fecth correctly
    */
    if (this.attr(`exclude-dimension-properties`)) {
      hiddenProperties = hiddenProperties.filter((prop) => prop.type !== `dimension`);
    }
    return [
      {
        type: MenuItemType.UserProperties,
        isLoading: this.state.loadingPeopleProperties,
        items: this.getPropertyMenuItems({properties: hiddenProperties, resourceType: ResourceType.User}),
        label: `Hidden Profile Properties`,
        showLimit: MENU_ITEM_LIMIT,
        isHidden: true,
      },
    ];
  }

  private isYourCohort(cohort) {
    return cohort.createdBy && cohort.createdBy.id === this.attr(`user-id`);
  }

  private transformCohortToCohortItem(cohort: SerializedCohort | SerializedInlineCohort): CohortItem {
    if (isSerializedInlineCohort(cohort as SerializedInlineCohort)) {
      const unsavedCohort = cohort as SerializedInlineCohort;

      // Hide converted cohorts if the saved cohort exists in store
      if (
        unsavedCohort.raw_cohort.unsavedId &&
        Number.isInteger(unsavedCohort.id) &&
        cohortsStore.value[unsavedCohort.id]
      ) {
        return null;
      }

      // Unsaved cohort does not contain permissions (e.g. canView)
      return {
        label: unsavedCohort.name,
        name: unsavedCohort.name,
        value: {
          cohort: {
            ...unsavedCohort,
          },
        },
        // https://mixpanel.atlassian.net/browse/SRFE-4562
        // but they should always be available for share
        canView: true,
        createdBy: unsavedCohort.created_by,
      };
    }

    const savedCohort = cohort as SerializedCohort;

    return {
      id: savedCohort.id,
      label: savedCohort.name,
      value: savedCohort.id,
      name: savedCohort.name,
      count: savedCohort.count,
      description: savedCohort.description,
      dataGroupId: savedCohort.data_group_id,
      createdBy: savedCohort.created_by,
      lastEdited: savedCohort.last_edited,
      can_update_basic: savedCohort.can_update_basic,
      canView: savedCohort.can_view,
      canUpdateBasic: savedCohort.can_update_basic,
      canShare: savedCohort.can_share,
      isSuperadmin: savedCohort.is_superadmin,
      allowStaffOverride: savedCohort.allow_staff_override,
    };
  }

  private getSerializedCohorts() {
    return sortBy(cohortsStore.value, `name`)
      .filter((cohort: SerializedCohort | SerializedInlineCohort) => {
        const dataGroupId = this.attr(`data-group-id`);
        const cohortDataGroupId = isSerializedInlineCohort(cohort)
          ? cohort.raw_cohort.data_group_id
          : cohort.data_group_id;
        return dataGroupIDsEqual(dataGroupId, cohortDataGroupId ? cohortDataGroupId.toString() : ``);
      })
      .map((cohort: SerializedCohort | SerializedInlineCohort) => this.transformCohortToCohortItem(cohort))
      .filter(Boolean);
  }

  private getCohortsMenuSections(): PropertySection[] {
    const cohorts = this.getSerializedCohorts();

    let inlineCohorts;
    let [userCohorts, allCohorts] = partition(cohorts, (cohort) => this.isYourCohort(cohort));
    // @ts-ignore - TODO: Type 'PropertyItem[]' is not assignable to type 'Cohort[]'.
    [inlineCohorts, userCohorts] = partition(
      this.getPropertyMenuItems({
        properties: userCohorts,
        resourceType: ResourceType.Cohort,
      }),
      (cohort) => cohort.value && cohort.value.cohort && cohort.value.cohort.raw_cohort,
    );

    allCohorts = allCohorts.filter((cohort) => cohort.canView);
    // @ts-ignore - TODO: Type 'PropertyItem[]' is not assignable to type 'Cohort[]'.
    allCohorts = this.getPropertyMenuItems({
      properties: allCohorts,
      resourceType: ResourceType.Cohort,
    });
    // @ts-ignore - TODO: CohortItem[] is not assignable to type 'PropertySectionItem[]'.
    allCohorts = this.sortPropertiesByScore(
      //@ts-ignore - TODO: CohortItem[] is not assignable to type 'PropertySectionItem[]'
      allCohorts.map((cohort) => ({
        ...cohort,
        // @ts-ignore - TODO: Type 'CohortItem is not assignable to type 'PropertySectionItem'.
        isRecentlyUsed: this.isPropertyRecentlyUsed(cohort),
      })),
    );

    if (this.attr(`show-all-users-cohort`)) {
      // @ts-ignore - TODO: Type 'PropertyItem[]' is not assignable to type 'Cohort[]'.
      allCohorts.unshift(ALL_USERS_COHORT);
    }

    const yourCohortsSection = {
      type: MenuItemType.Cohorts,
      isLoading: this.state.loadingCohorts,
      items: [
        ...(this.canCreateInlineCohorts() && !this.isBetaMenu() ? [SPECIAL_CONTROLS.CREATE_INLINE_COHORT] : []),
        ...inlineCohorts,
        ...userCohorts,
      ],
      label: `Your Cohorts`,
    };

    const otherCohortsSection = {
      type: MenuItemType.Cohorts,
      isLoading: this.state.loadingCohorts,
      // @ts-ignore - TODO: Type 'CohortItem[]' is not assignable to type 'PropertyItem[]'.
      items: allCohorts,
      label: this.isBetaMenu() ? null : `All Cohorts`,
      showLimit: userCohorts.length > 0 ? MENU_ITEM_LIMIT : null,
    };

    if (this.isBetaMenu()) {
      return [yourCohortsSection, otherCohortsSection];
    } else {
      // defines cohorts that were last selected and currently selected, the culprit to check if a cohort goes all the way
      // to the bottom without being categorized for some reason
      const lastMultiSelection = Array.isArray(this.attr(`selected`)) ? this.attr(`selected`) : [];

      const currentlySelected = intersectionBy(this.state.selected, lastMultiSelection, `value`);
      const selectedCohorts = this.getPropertyMenuItems({
        properties: cohorts,
        resourceType: ResourceType.Cohort,
        selected: currentlySelected,
      }).filter((cohort) => cohort.isSelected);

      const selectedCohortsSection = {
        type: MenuItemType.Cohorts,
        isLoading: this.state.loadingCohorts,
        items: selectedCohorts,
        label: `Selected Cohorts`,
      };

      return [selectedCohortsSection, yourCohortsSection, otherCohortsSection];
    }
  }

  private getOtherMenuSection(): PropertySection {
    const items = this.attr(`other-options`)
      .filter((item) => OTHER_OPTIONS.has(item.value))
      .map((item) => {
        // Behaviors are computed in a special way that creates a separate prop down the line. We want to normalize it first in that case
        const normalizedSelection = this.state.selected?.behavior
          ? getMenuItemForBehavior(this.state.selected.behavior)
          : this.state.selected;
        const isSelected = this.isItemSelected(item.value, normalizedSelection);
        const isDisabled = this.isPropertyDisabled(item, item.value);
        const dataGroupId = this.attr(`data-group-id`);
        const dataGroups = Object.values(this.state.dataGroups);
        const itemLabel = hasDynamicLabel(item)
          ? formatDataGroupLabel({label: item.dynamicLabel, dataGroupId, dataGroups})
          : item.label;
        const mappedItem = {
          ...item,
          label: itemLabel,
          tooltipData: {
            ...item.tooltipData,
            header: itemLabel,
          },
          isSelected,
          isDisabled,
          ...(isDisabled ? {hasCaret: false, tooltip: {text: this.attr(`disabled-items`)[item.value]}} : {}),
        };
        return mappedItem;
      });
    return {
      isLoading: false,
      items,
      label: this.attr(`hide-section-labels`) ? null : `Other`,
    };
  }

  /**
   * Generates items-menu sections from each loaded data group
   */
  private getDataGroupsSections(dataGroupId?: string): PropertySection[] {
    const reportDataGroupId = this.attr(`data-group-id`);
    return this.getVisibleDataGroups(dataGroupId).map((group) => {
      const displayDataGroupFirstEventTime =
        this.isFirstEventTimeEnabled() && dataGroupIDsEqual(reportDataGroupId, group.groupID);
      const properties = this.sortProperties([
        // all data group properties
        ...group.properties,
        /// special
        ...(displayDataGroupFirstEventTime ? [FIRST_EVENT_TIME_PROPERTY] : []),
      ]);

      return {
        isLoading: group.isLoading,
        items: this.getPropertyMenuItems({
          dataGroupId: group.groupID,
          properties: properties as PropertySectionItem[],
          resourceType: ResourceType.User,
        }),
        label: this.attr(`hide-section-labels`) ? null : `${group.displayName} Properties`,
        showLimit: MENU_ITEM_LIMIT,
      };
    });
  }

  private getVisibleDataGroups(dataGroupId?: string): DataGroupItem[] {
    const {dataGroups} = this.state;
    const singleDataGroup = this.attr(`single-data-group`);
    let groups: DataGroupItem[] = dataGroupId
      ? dataGroups[dataGroupId]
        ? [dataGroups[dataGroupId]]
        : []
      : Object.values(dataGroups);

    if (singleDataGroup) {
      groups = groups.filter((group) => group.groupID === singleDataGroup);
    }

    return groups;
  }

  /**
   * Generates all tabs to be displayed in the selection menu
   */
  public getToggleTabs(): ToggleOption[] {
    const tabs = [];
    // show toggles when nothing is staged
    const canShowToggles = !this.hasStagedItem();
    if (canShowToggles) {
      const showEvents = this.attr(`menu-item-types`).includes(MenuItemType.Events);
      const showEventProperties = this.shouldShowEventProps();
      const showUserProperties = this.shouldShowUserProps();
      const hasOtherOptions = this.attr(`other-options`).length > 0;
      const showCohorts = this.shouldShowCohorts();

      if (this.isBetaMenu()) {
        tabs.push({value: AllPropertiesTab, label: `Properties`, disable: false});
      } else {
        // event properties
        if (showEventProperties) {
          tabs.push({value: ResourceType.Event, label: `Event`, disabled: false});
        }

        // user properties
        if (showUserProperties) {
          tabs.push({value: ResourceType.User, label: `User`, disabled: false});
        }

        // events
        showEvents && tabs.push({value: MenuItemType.Events, label: `Did Event`, disabled: false});

        // data group tabs
        for (const group of this.getVisibleDataGroups()) {
          tabs.push({label: group.displayName, value: group.groupID, disabled: false});
        }

        // cohorts
        if (showCohorts) {
          tabs.push({value: ResourceType.Cohort, label: `Cohorts`, disabled: false});
        }

        if (this.attr(`show-all-tab`) && (tabs.length > 1 || hasOtherOptions)) {
          tabs.unshift({value: AllTab, label: `All`, disabled: false});
        }
      }
    }
    return tabs;
  }

  private eventNames(): string[] {
    // if the staged item stack is comprised of an event then we are looking at a diff set of properties
    const item = this.state.stagedItemsStack.find((item) => item.menuItemType === MenuItemType.EventWithProperties);
    if (item) {
      return [item.value.value];
    }
    return this.attr(`event-names`) && uniq<string>(this.attr(`event-names`)).filter(Boolean);
  }

  eventPropertiesDefinitionsStoreCallback = () => {
    this.update({loadingEventProperties: false});
  };

  globalEventPropertiesDefinitionsStoreCallback = () => {
    this.update({loadingGlobalEventProperties: false});
    this.getTooltipData.cache.clear();
  };

  /**
   * Fetches event properties for a set of events or all events
   */
  private async fetchEventProperties() {
    const eventNames = this.eventNames();
    if (this.shouldShowEventProps()) {
      const includeDimensionProperties = !this.attr(`exclude-dimension-properties`);

      // Tooltip data currently depends on global event property definitions.
      // We need to ensure global properties are subscribed and updated.
      this.update({loadingGlobalEventProperties: true});
      propertyDefinitionsForEventsStore.unsubscribeAll(this.globalEventPropertiesDefinitionsStoreCallback);
      await fetchGlobalEventPropertyDefinitions({callback: this.globalEventPropertiesDefinitionsStoreCallback});

      if (this.shouldFilterToEventPropertyIntersection()) {
        this.update({loadingEventProperties: true});
        await Promise.all(
          eventNames.map((name) => fetchPropertyDefinitionsForEvents([name], {includeDimensionProperties})),
        );

        // We cannot use store subscription callbacks for multiple queries.
        // Check that events have not changed before clearing the loading state.
        if (isEqual(eventNames, this.eventNames())) {
          this.eventPropertiesDefinitionsStoreCallback();
        }
      } else if (!isQueryOnAllEvents(eventNames)) {
        this.update({loadingEventProperties: true});
        propertyDefinitionsForEventsStore.unsubscribeAll(this.eventPropertiesDefinitionsStoreCallback);
        const propertyCallsToMake = [
          fetchPropertyDefinitionsForEvents(eventNames, {
            includeDimensionProperties,
          }),
        ];
        if (this.attr(`scd-poc-enabled`)) {
          // Make separate call for SCD so we can look it up individually later
          propertyCallsToMake.push(
            fetchPropertyDefinitionsForEvents([SCD_EVENT_NAME], {
              includeDimensionProperties,
            }),
          );
        }
        await Promise.all(propertyCallsToMake);
        this.eventPropertiesDefinitionsStoreCallback();
      }
    }
  }

  /**
   * Updates a given dataGroup with its list of properties from the store - otherwise fetches and then updates
   */
  private async fetchDataGroupResourceProperties(resourceType: ResourceType, dataGroupId: string) {
    let properties = [];
    const dataGroup = this.state.dataGroups[dataGroupId];
    const params = {
      dataGroupId,
      projectId: this.sourceAttrs.projectId,
      workspaceId: this.sourceAttrs.workspaceId,
    };

    try {
      if (resourceType === ResourceType.User) {
        // If store isn't populated by this point, await the fetch
        if (Object.keys(groupPropertyDefinitionsStore.value).length === 0) {
          await fetchAllPropertyDefinitionsForGroupProfiles(dataGroupStore.value);
        }
        properties = Object.values(groupPropertyDefinitionsStore.value).filter(
          (groupProperty) => groupProperty.dataGroupId === dataGroupId,
        );
      } else if (resourceType === ResourceType.Event) {
        properties = await eventPropertiesClient.query(params);
      } else {
        throw new Error(`Failed to fetch properties for invalid resource type ${resourceType}`);
      }
    } catch (error) {
      console.error(`Error loading data group (${dataGroupId}) user properties`, error);
    }

    this.update({
      dataGroups: {
        ...this.state.dataGroups,
        [dataGroupId]: {
          ...dataGroup,
          isLoading: false,
          properties,
        },
      },
    });
  }

  /**
   * Loads all data groups from the store and triggers their
   * respective properties request
   */
  private loadDataGroups() {
    if (!this.attr(`all-data-groups`)) {
      return this.update({dataGroups: {}});
    }
    const dataGroups: PropertiesMenuState['dataGroups'] = {};

    const groups = Object.values(dataGroupStore.value);
    for (const group of groups) {
      const dataGroupId = group.groupID;
      const cachedProperties = peoplePropertiesClient.getCachedResponse({
        dataGroupId,
        projectId: this.sourceAttrs.projectId,
        workspaceId: this.sourceAttrs.workspaceId,
      });

      dataGroups[group.groupID] = {
        ...group,
        isLoading: cachedProperties ? false : true,
        properties: cachedProperties || [],
      };
    }

    // only fetch properties for data groups that are not cached
    this.update({dataGroups});
    groups.forEach(({groupID}) => {
      if (dataGroups[groupID].isLoading) {
        this.fetchDataGroupResourceProperties(ResourceType.User, groupID);
      }
    });
  }

  private async fetchLookupTables() {
    const shouldFetchLookupTables =
      !this.attr(`exclude-lookup-table-properties`) && !this.attr(`exclude-dimension-properties`);
    if (shouldFetchLookupTables) {
      this._lookupTablesStoreCallback = ({fromUpdate}) => {
        if (fromUpdate) {
          // callback triggered from manual #update call (e.g. mapping registered or unregistered)
          return;
        }
        this.registerTempLookupTableMappingFromSelected();
        fetchAllLookupTableProperties();
        this.getTooltipData.cache.clear();
      };
      lookupTablesStore.subscribe(this._lookupTablesStoreCallback);

      if (!lookupTablesStore.hasFetchedValue()) {
        await lookupTablesStore.fetch({
          ...(this.attr(`project-id`) ? {projectId: this.attr(`project-id`)} : {}),
          ...(this.attr(`workspace-id`) ? {workspaceId: this.attr(`workspace-id`)} : {}),
        });
      }
    }
  }

  private fetchUserDetails() {
    return userDetailsStore.fetch();
  }

  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  private async fetchCohorts() {
    if (!this.attr(`project-has-cohorts`)) {
      return;
    }

    this.update({loadingCohorts: true});

    // Handle unsaved -> saved conversion on store change
    this._cohortStoreCallback = () => {
      this.update({loadingCohorts: false});
      this.getTooltipData.cache.clear();

      const savedCohortItems = this.getSavedCohortSelection();
      savedCohortItems.forEach((item, index) => {
        const canonicalCohort = cohortsStore.value[item.value] as SerializedCohort;
        if (item.label && canonicalCohort && canonicalCohort.name !== item.label) {
          this.replaceItem(
            {
              resourceType: ResourceType.Cohort,
              label: canonicalCohort.name,
              value: item.value,
            },
            index,
            {autoTriggered: true},
          );
        }
      });

      const inlineCohorts = this.getInlineCohortSelection();
      inlineCohorts.forEach(({value}, index) => {
        if (!value.cohort.raw_cohort.unsavedId) {
          return;
        }

        const unsavedCohort = cohortsStore.value[value.cohort.raw_cohort.unsavedId] as SerializedInlineCohort;

        if (!unsavedCohort) {
          return;
        }

        // Inline cohort converted
        if (Number.isInteger(unsavedCohort.id)) {
          const cohort = cohortsStore.value[unsavedCohort.id];

          this.replaceItem(this.getCohortPropertyItem(cohort.name, cohort.created_by, cohort.id), index, {
            autoTriggered: true,
          });
        } else if (!isEqual(unsavedCohort.raw_cohort, value.cohort.raw_cohort)) {
          // Inline cohort updated
          this.replaceItem(
            this.getCohortPropertyItem(unsavedCohort.name, unsavedCohort.created_by, {cohort: unsavedCohort}),
            index,
            {autoTriggered: true},
          );
        }
      });
    };

    const dataGroupId = this.attr(`data-group-id`);
    if (dataGroupId && this.attr(`support-b2b-cohorts`)) {
      cohortsStore.subscribe(this._cohortStoreCallback, dataGroupId);
      await cohortsStore.fetch(
        {
          projectId: this.attr(`project-id`),
          workspaceId: this.attr(`workspace-id`),
          dataGroupId,
        },
        {},
        dataGroupId,
      );
    } else {
      cohortsStore.subscribe(this._cohortStoreCallback);
      await cohortsStore.fetch({
        projectId: this.attr(`project-id`),
        workspaceId: this.attr(`workspace-id`),
      });
    }
  }

  private getPropertyMenuItems({
    properties,
    dataGroupId,
    resourceType,
    selected,
    propertyDefaultType,
  }: {
    properties?: Array<CohortItem | PropertySectionItem | PropertyDefinition | SpecialControl | Selection>;
    dataGroupId?: string;
    resourceType?: ResourceType;
    selected?: any;
    propertyDefaultType?: PropertyType;
  } = {}): PropertySectionItem[] {
    const propertyDefinitionsForEventsStoreKey = this.getPropertyDefinitionsForEventsStoreKey();

    let filterTypes = this.hasStagedItem() ? [] : this.attr(`type-filter`);

    // only show numeric properties for numeric computed options
    if (this.getStagedItemType() === StagedItemType.ComputedListNumericProperty) {
      filterTypes = [PropertyType.Number];
    }

    if (filterTypes.length) {
      properties = properties.filter((prop) => `type` in prop && filterTypes.includes(prop.type));
    }

    const items = properties.map((property) =>
      this.getPropertyMenuItem({
        dataGroupId,
        property,
        resourceType,
        selected,
        propertyDefaultType,
        propertyDefinitionsForEventsStoreKey,
      }),
    );

    return items;
  }

  private isPropertyDisabled(property, propertyValue) {
    if (property?.customPropertyId && !isPersistedCustomProperty(property?.customPropertyId)) {
      return false;
    }

    return Boolean(
      Object.prototype.hasOwnProperty.call(this.attr(`disabled-items`), propertyValue) ||
        (!this.attr(`selectable-sensitive-properties`) && !this.isPropertyAccessible(property)),
    );
  }

  private isPropertyAccessible(property) {
    return currentProjectStore.value?.hasSensitiveAccess || !property.sensitive;
  }

  private isPropertyRecentlyUsed(property: PropertySectionItem | Item) {
    if (!this._recentlyUsedUniqueKeys) {
      this._recentlyUsedUniqueKeys = new Set(
        [ResourceType.Event, ResourceType.User, ResourceType.Cohort].flatMap((resourceType) => {
          return this.getRecentItems(resourceType).map((item) => {
            return getItemUniqueKey({...item, resourceType});
          });
        }),
      );
    }
    return this._recentlyUsedUniqueKeys.has(getItemUniqueKey(property));
  }

  private getPropertyDefinitionsForEventsStoreKey(): string {
    return eventDefinitionParamsAndKey(this.eventNames(), {
      includeDimensionProperties: false,
      projectId: this.sourceAttrs.projectId.toString(),
      workspaceId: this.sourceAttrs.workspaceId.toString(),
    }).key;
  }

  /**
   * This function basically massages a variety of inputs into a normalized "PropertySectionItem".
   */
  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  private getPropertyMenuItem({
    property,
    dataGroupId,
    resourceType,
    selected,
    propertyDefaultType,
    propertyDefinitionsForEventsStoreKey,
  }: {
    property: CohortItem | PropertySectionItem | PropertyDefinition | SpecialControl | Selection;
    dataGroupId?: string;
    resourceType?: ResourceType;
    selected?: any;
    propertyDefaultType?: PropertyType;
    // Since computing the key is expensive, it can be passed in as a perf optimization if this method is called for
    // a list of property items
    propertyDefinitionsForEventsStoreKey?: string;
  }): PropertySectionItem {
    const forCohorts = resourceType === ResourceType.Cohort;

    if (`resourceType` in property && property.resourceType) {
      // custom properties/formulas specify their own resource types
      resourceType = property.resourceType;
    }
    selected = selected || this.state.selected;

    // if we are in an inner menu then we can look into the selected behavior for the property
    if (this.state.stagedItemsStack.length && this.state.selected?.behavior) {
      selected = this.state.selected?.behavior?.property;
    }

    const isBetaMenu = this.isBetaMenu();

    const subLabelTheme = `subLabelTheme` in property && property.subLabelTheme;
    const isControlItem = `isControlItem` in property && property.isControlItem;
    const customPropertyId = `customPropertyId` in property && property.customPropertyId;
    const customProperty = `customProperty` in property && property.customProperty;

    // TODO: this is kind of crazy
    let name = `name` in property && property.name;
    const value = (`value` in property && property.value) || name || (customProperty && customProperty.name);
    let label = `label` in property && property.label;
    label = label || value;
    name = name || value;

    propertyDefinitionsForEventsStoreKey =
      propertyDefinitionsForEventsStoreKey ?? this.getPropertyDefinitionsForEventsStoreKey();
    const dataDefinition = (propertyDefinitionsForEventsStore?.value[propertyDefinitionsForEventsStoreKey] || {})[name];

    const customPropertyIdMatches = selected?.customPropertyId && selected.customPropertyId === customPropertyId;
    const dimensionDataGroupIdMatches =
      (selected?.dataGroupId && `dataGroupId` in property && selected.dataGroupId === property.dataGroupId) ||
      (selected?.tempDataGroupId && `dataGroupId` in property && selected.tempDataGroupId === property.dataGroupId);
    const dimensionPropertyMatches =
      dimensionDataGroupIdMatches &&
      (!selected?.customPropertyId || customPropertyIdMatches) &&
      [selected.value, `value` in property && property.value].every(Array.isArray) &&
      selected.value?.[0] === (`value` in property && property.value?.[0]) &&
      selected.value?.[1] === (`value` in property && property.value?.[1]);
    const bothAreJoinKeys =
      selected?.joinPropertyType &&
      `joinPropertyType` in property &&
      property.joinPropertyType &&
      !Array.isArray(selected.value) &&
      // property.value is currently just omitted for join keys, but testing if it's an array in case that changes
      `value` in property &&
      !Array.isArray(property.value);
    // selected.dataGroupId is currently omitted for join keys; joinPropertyType is the only way to tell it's a dimension prop
    const noDimensionDataGroup =
      !selected?.joinPropertyType && !(`joinPropertyType` in property && property.joinPropertyType);
    const isSelected =
      (forCohorts && this.isItemSelected(value, selected)) ||
      (Array.isArray(selected) && this.isItemSelected(value, selected)) ||
      (selected && dataGroupId && dataGroupId === selected.dataGroupId && selected.value === value) ||
      dimensionPropertyMatches ||
      (selected &&
        (noDimensionDataGroup || bothAreJoinKeys) &&
        selected.value === value &&
        selected.resourceType === resourceType &&
        selected.dataGroupId === dataGroupId) ||
      (noDimensionDataGroup && (noDimensionDataGroup || bothAreJoinKeys) && customPropertyIdMatches) ||
      false;
    const appliedDataGroupId = `dataGroupId` in property && property.dataGroupId ? property.dataGroupId : dataGroupId;

    const type = this.getPropertyType(name, property, isSelected && selected);
    const propertyType =
      propertyDefaultType || (isSelected && selected.propertyDefaultType) || (`type` in property && property.type);
    const isDisabled = this.isPropertyDisabled(property, value);
    const isAccessible = this.isPropertyAccessible(property);
    const tooltip = !isAccessible ? {text: DISABLED_DUE_TO_CLASSIFIED_DATA} : null;
    const isDimension = type === PropertyType.Dimension;
    const isObject = type === PropertyType.Object;
    const includeLookupTableProperties = isDimension && this.canStageItemType(StagedItemType.Dimension);

    let allowPill = this.attr(`allow-typecast-pill-action`) === TypecastPillAction.Typecast;
    if (this.attr(`allow-typecast-pill-action`) === TypecastPillAction.TriggerTutorial) {
      allowPill = !isBetaMenu;
    }
    const canTypecastProperty = !forCohorts && !isDimension && !isControlItem && isAccessible;

    let menuItemType;
    if (isDimension) {
      menuItemType = MenuItemType.LookupTableProperties;
    } else {
      switch (resourceType) {
        case ResourceType.Event:
          if (customProperty || customPropertyId) {
            menuItemType = MenuItemType.EventCustomProperties;
          } else {
            menuItemType = MenuItemType.EventProperties;
          }
          break;
        case ResourceType.User:
          if (customProperty || customPropertyId) {
            menuItemType = MenuItemType.UserCustomProperties;
          } else if (appliedDataGroupId) {
            menuItemType = MenuItemType.DataGroupProperties;
          } else {
            menuItemType = MenuItemType.UserCustomProperties;
          }
          break;
        case ResourceType.Cohort:
          menuItemType = MenuItemType.Cohorts;
          break;
        case ResourceType.Other:
          menuItemType = MenuItemType.Other;
          break;
        case ResourceType.Formula:
          throw Error(`Unexpected resource type in getPropertyMenuItem: ${resourceType}`);
        default:
          assertUnreachable(resourceType);
      }
    }

    const basePropertySectionItem: SectionItem<any, PropertyItemState> = {
      ...(`dimensionDataGroupId` in property && property.dimensionDataGroupId
        ? {dimensionDataGroupId: property.dimensionDataGroupId}
        : {}),
      ...(`tooltipData` in property && property.tooltipData ? {tooltipData: property.tooltipData} : {}),
      hasCaret:
        (includeLookupTableProperties || isObject || this.attr(`show-items-caret`)) &&
        !isControlItem &&
        isAccessible &&
        !this.helpers.useMultipleSelection(),
      persistCaret: isObject && isBetaMenu && !this.helpers.useMultipleSelection(),
      caretStyle: `arrow`,
      hasPill: allowPill && canTypecastProperty,
      hidden: !!(`hidden` in property && property.hidden),
      icon:
        (`icon` in property && property.icon) ||
        (this.isBetaMenu() &&
          forCohorts &&
          !(this.state.selectedFilter?.resourceType === ResourceType.Cohort && this.helpers.useMultipleSelection()) &&
          `community`) ||
        getIconForPropertyType(type, {isCustom: isBetaMenu && Boolean(customProperty)}),
      isControlItem,
      isDisabled,
      // Don't clutter up data unless slowly changing dimensions poc is enabled
      ...(this.attr(`scd-poc-enabled`)
        ? {
            isScd: `isScd` in property && property.isScd,
          }
        : {}),
      tooltip,
      isSelected,
      label,
      name,
      pillAction: PropertyItemAction.Typecast,
      pillLabel: `Typecast`,
      densityLocal: `densityLocal` in property && property.densityLocal,
      densityGlobal: `densityGlobal` in property && property.densityGlobal,
      count: `count` in property && property.count,
      uiQueryCount: `uiQueryCount` in property && property.uiQueryCount,
      uiUniqueQueryCount: `uiUniqueQueryCount` in property && property.uiUniqueQueryCount,
      ...(`joinPropertyLabel` in property && property.joinPropertyLabel
        ? {subLabel: property.joinPropertyLabel}
        : {subLabel: `subLabel` in property && property.subLabel}),
      isLookupTableProperty: `isLookupTableProperty` in property && property.isLookupTableProperty,
      ...(subLabelTheme ? {subLabelTheme} : {}),
      menuItemType,
    };

    let propertyItem: PropertySectionItem;

    if (resourceType === ResourceType.Cohort) {
      let item: Item;
      if (typeof value === `number`) {
        item = {
          resourceType: ResourceType.Cohort,
          label,
          value,
        };
      } else if (value) {
        const cohortDef = value as InlineCohortSegment;
        item = {
          resourceType: ResourceType.Cohort,
          label,
          value: cohortDef,
        };
      }
      // TODO: I'm not sure this is the right thing to do here (putting these on the PropertySectionItem)
      // but we use this data when generating the cohort tooltip so keeping it this way for now. For properties,
      // we look up the definition data from the store inside the util.
      // Prior to this change we were just blindly expanding `{...property}` which originally is of type Cohort
      // in this case. I'd rather be explicit about what we're doing and why.
      const cohortExtras: CohortExtras = {
        description: `description` in property && property.description,
        canView: `canView` in property && property.canView,
        canUpdateBasic: `canUpdateBasic` in property && property.canUpdateBasic,
        canShare: `canShare` in property && property.canShare,
        isSuperadmin: `isSuperadmin` in property && property.isSuperadmin,
      };

      propertyItem = Object.assign(basePropertySectionItem, item, cohortExtras);
    } else {
      const item = {
        ...(appliedDataGroupId ? {dataGroupId: appliedDataGroupId} : {}),
        ...(customPropertyId ? {customPropertyId} : {}),
        ...(customProperty ? {customProperty} : {}),
        ...(`tempDataGroupId` in property && property.tempDataGroupId
          ? {tempDataGroupId: property.tempDataGroupId}
          : {}),
        ...(`dimensionDataGroupId` in property && property.dimensionDataGroupId
          ? {dimensionDataGroupId: property.dimensionDataGroupId}
          : {}),
        ...(`tooltipData` in property && property.tooltipData ? {tooltipData: property.tooltipData} : {}),
        ...(`joinPropertyType` in property && property.joinPropertyType
          ? {joinPropertyType: property.joinPropertyType}
          : {}),
        label,
        propertyDefaultType: propertyType,
        resourceType,
        type,
        value,
        ...(`joinPropertyLabel` in property && property.joinPropertyLabel
          ? {subLabel: property.joinPropertyLabel}
          : {subLabel: `subLabel` in property && property.subLabel}),
      };
      propertyItem = Object.assign(basePropertySectionItem, item);
    }

    if (isBetaMenu) {
      if (isProperty(propertyItem) || isCohort(propertyItem)) {
        const isProfilePropertyMenu = !this.shouldShowEventProps();
        const propertyDataGroupId =
          (propertyItem.resourceType === ResourceType.User && propertyItem.dataGroupId) || null;
        const selectedDataGroupId = this.attr(`data-group-id`) || null;
        const isPropertyForSelectedProfileType =
          propertyItem.resourceType === ResourceType.User && propertyDataGroupId === selectedDataGroupId;
        const isShowingAll = this.state.selectedFilter?.value === `all`;
        const includeResourceLabel = isShowingAll && (!isProfilePropertyMenu || !isPropertyForSelectedProfileType);
        propertyItem.subLabel = getPropertyPathPrefix(propertyItem, Object.values(this.state.dataGroups), {
          includeResourceLabel,
        });
        propertyItem.excludeFromInputMatch = resourceType !== ResourceType.Event;
      }
      propertyItem.subLabelTheme = SubLabelTheme.Breadcrumb;
      // we put label in here twice so that a user could do fuzzy matching in either order (label then sublabel or sublabel then label)
      // Imagine a lookup table path like "email.domain", a user can do "domemail" or "emaildom" to match it.
      propertyItem.searchableContent = [
        dataDefinition?.name,
        propertyItem.label,
        propertyItem.subLabel,
        propertyItem.label,
      ].join(` `);
    }
    propertyItem.isRecentlyUsed = this.isPropertyRecentlyUsed(propertyItem);
    return propertyItem;
  }

  public getSearchFilter(): string {
    return this.state.searchFilter;
  }

  private getPropertyType(
    name: string,
    property: CohortItem | PropertySectionItem | PropertyDefinition | SpecialControl | Selection,
    selected?: PropertySectionItem,
  ): PropertyType {
    const type = `type` in property && property.type;

    if (selected) {
      if (`joinPropertyType` in selected && selected.joinPropertyType === PropertyType.Object) {
        return PropertyType.Object;
      }

      if (`type` in selected && selected.type && type !== PropertyType.Dimension) {
        return selected.type;
      }
    }

    const castToString =
      STRING_TYPECAST_PROPERTIES.includes(name) ||
      (this.params.listProperty && [PropertyType.List, PropertyType.Object].includes(type));

    return castToString ? PropertyType.String : type;
  }

  private getArraySelection() {
    const selected = this.state.selected;

    if (Array.isArray(selected)) {
      return selected;
    }

    return [];
  }

  private isDimensionSelected(selected) {
    if (!selected) {
      return false;
    }
    // lookup table (aka dimension property) selected.value is an array of two strings (vs b2b profiles)
    return (selected.dataGroupId || selected.tempDataGroupId) && Array.isArray(selected.value);
  }

  private isItemSelected(value, selection: Selection) {
    const getCompareValue = (v) => (v?.cohort?.raw_cohort ? v.cohort.raw_cohort.unsavedId : v);

    if (Array.isArray(selection)) {
      return selection.some((item) => getCompareValue(item.value) === getCompareValue(value));
    }

    return selection && getCompareValue(selection.value) === getCompareValue(value);
  }

  private async editCohort(item) {
    const cohortParsed = item.id ? cohortsStore.value[item.id] : item.value?.cohort?.raw_cohort || item.value?.cohort;
    const cohort = CohortModel.parseCohort(cohortParsed);
    const {cohortStagedValue, cohortSavedValue} = await this.promptInlineCohortsModal(cohort);

    this.handleCohortUpdate(cohortStagedValue, cohortSavedValue);
    if (cohortStagedValue?.cohort || cohortSavedValue?.cohort) {
      this.trackEvent(TrackingEvent.OpenInlineCohortsModal, {
        action: `Edit`,
        context: this._context,
      });
    }
  }

  /**
   * Adding a temporary cohort, editing a temporary cohort, saving a new cohort directly,
   * or saving a temporary cohort
   * @param {object} [options]
   * @param {number} [options.id] - id of the cohort, only applicable if saving the cohort
   * @param {CohortModel} [options.cohort] - cohort being passed in, only exists if not saving the cohort
   * @param {string} [options.name] - name of the cohort
   * @param {boolean} [options.savedAsNew] - whether we are saving the cohort as new
   *
   * Adding a new temporary cohort - name, cohort
   * []Editing a temporary cohort - name, cohort
   * []Saving an existing temporary cohort - id, name, cohort
   * Saving a new cohort / editing an existing cohort - id, name, cohort, savedAsNew = false
   * Saving an existing cohort as new - id, name, cohort, savedAsNew = true
   */
  private commitCohort({id, cohort, name, savedAsNew}: InlineCohortsModalCommitEvent['detail']) {
    let itemCohort: SerializedCohort | SerializedInlineCohort;
    const isSaved = Boolean(id);
    const isEditingTemporaryCohort = Boolean(cohort.unsavedId);
    /**
     * cohort.unsavedId
     * "" - saving a new cohort
     * null - updating or saving an existing cohort as new
     * a string - editing or saving an existing temporary cohort
     */
    const unsavedId = cohort.unsavedId || nanoid();
    // applies to an existing and temporary cohort, both are considered saved states
    if (isSaved) {
      const item = this.getCohortPropertyItem(name, this.getCreatedBy(), id);
      this.replaceOrSelectCohort(item, isEditingTemporaryCohort || isSaved, unsavedId);
    } else {
      itemCohort = {
        name,
        raw_cohort: {
          ...cohort.serialize(),
          unsavedId,
          ...(userDetailsStore.value
            ? {
                createdBy: {
                  id: this.attr(`user-id`),
                  name: userDetailsStore.value.name,
                  email: userDetailsStore.value.email,
                },
              }
            : {}),
        },
      } as SerializedInlineCohort;
      const item = this.getCohortPropertyItem(name, this.getCreatedBy(), {cohort: itemCohort});
      this.replaceOrSelectCohort(item, isEditingTemporaryCohort, unsavedId);
    }

    if (!isSaved) {
      // updating cohort store for cohorts that are temporary
      const unsavedCohort = itemCohort as SerializedInlineCohort;
      cohortsStore.update({
        [unsavedId]: {
          name,
          raw_cohort: unsavedCohort.raw_cohort,
          created_by: cohort.createdBy,
        } as SerializedInlineCohort,
      });
    } else if (isEditingTemporaryCohort && isSaved) {
      // Mark unsaved cohort being converted to a saved cohort
      cohortsStore.update({
        [unsavedId]: {
          name,
          created_by: cohort.createdBy,
          ...(cohortsStore.value[unsavedId] || {}),
          id: cohort.id,
        } as SerializedInlineCohort,
      });
    }
    const isEditingExistingCohort = cohort.unsavedId === null && savedAsNew;
    this.trackInlineCohorts(name, isSaved, isEditingTemporaryCohort, isEditingExistingCohort, savedAsNew);
  }

  private trackInlineCohorts(name, isSaved, isEditingTemporaryCohort, isEditingExistingCohort, savedAsNew) {
    if (isSaved) {
      this.trackEvent(TrackingEvent.SaveInlineCohorts, {
        context: this._context,
        name,
        // Indicates whether an applied inline cohort was later saved
        converted: !!isEditingTemporaryCohort,
        savedAsNew,
      });
    } else if (!isEditingTemporaryCohort) {
      this.trackEvent(TrackingEvent.CreateInlineCohort, {
        context: this._context,
        name,
      });
    } else {
      this.trackEvent(TrackingEvent.EditInlineCohorts, {
        context: this._context,
        name,
        existing: isEditingExistingCohort,
      });
    }
  }

  // add cohort to the selected attr, either replace an existing selected cohort, or add a new cohort
  // to the selected properties menu
  private replaceOrSelectCohort(item: PropertySectionItem, isEditingCohort: boolean, unsavedId: string) {
    const selected = this.state.selected;

    // based on internal user feedback cohorts created inline
    // should be committed much like custom props with breakdowns
    const index = Array.isArray(selected)
      ? this.getArraySelection().findIndex(
          (selectedItem) =>
            (isInlineCohort(selectedItem) && selectedItem.value.cohort?.raw_cohort?.unsavedId === unsavedId) ||
            selectedItem.value === item.value,
        )
      : isEqual(selected?.value, item.value);

    if (isEditingCohort && selected && index > -1) {
      this.replaceItem(item, index, {commit: true});
    } else {
      this.selectItem(item, {commit: true});
    }
  }

  private getCreatedBy(): User {
    return {
      id: this.attr(`user-id`),
      name: userDetailsStore?.value.name,
      email: userDetailsStore?.value.email,
    };
  }

  private getCohortPropertyItem(name: string, createdBy: User, value: TODO) {
    return {
      type: PropertyType.List,
      propertyDefaultType: PropertyType.List,
      resourceType: ResourceType.Cohort,
      label: name,
      value,
      createdBy,
    } as PropertySectionItem;
  }

  private getSavedCohortSelection(): SavedCohortItem[] {
    const selected = this.state.selected;
    const cohorts = Array.isArray(selected) ? selected : [selected];

    return cohorts.filter(
      (item): item is SavedCohort => item?.resourceType === ResourceType.Cohort && Number.isInteger(item?.value),
    );
  }

  private getInlineCohortSelection(): InlineCohort[] {
    const selected = this.state.selected;
    const cohorts = Array.isArray(selected) ? selected : [selected];

    return cohorts.filter(
      (item): item is InlineCohort =>
        !!(isInlineCohort(item) && item.value && item.value.cohort && item.value.cohort.raw_cohort),
    );
  }

  private getInlineCohortItems() {
    return this.getInlineCohortSelection().map((item) => ({
      ...item,
      createdBy: item.value.cohort.raw_cohort.createdBy,
      resourceType: ResourceType.Cohort,
    }));
  }

  private getSelectedCustomFormula(resourceType) {
    let selected = this.state.selected || [];
    if (!Array.isArray(selected)) {
      selected = [selected];
    }

    return selected.filter(
      (item) =>
        isProperty(item) &&
        item.resourceType === resourceType &&
        item.customPropertyId &&
        !isPersistedCustomProperty(item.customPropertyId),
    );
  }

  private registerTempLookupTableMappingFromSelected() {
    const selected = this.state.selected;
    if (!Array.isArray(selected) && isProperty(selected)) {
      const tempDataGroupId = selected?.tempDataGroupId;
      if (tempDataGroupId) {
        registerTempLookupTableMapping(selected, tempDataGroupId);
      }
    }
  }

  private addTempMappingsToProperties(
    resourceType: ResourceType.Event | ResourceType.User,
    custom: boolean,
    storeProperties,
  ) {
    const mappings = getTempLookupTableMappings(resourceType, custom);
    return storeProperties.map((storeProperty) => {
      // storeProperty is of type PropertyItem ('name' field used, 'value' field not used for name),
      // but the store is typed as Record<string,any>
      const value = custom ? storeProperty.customPropertyId : storeProperty.name;
      if (mappings.has(value)) {
        const tempDataGroupId = mappings.get(value);
        const joinPropertyType = storeProperty.joinPropertyType || storeProperty.type;
        return {...storeProperty, tempDataGroupId, type: PropertyType.Dimension, joinPropertyType};
      } else {
        return storeProperty;
      }
    });
  }

  private filterMultiAttributionProperties<T extends {type?: PropertyType}>(items: T[]): T[] {
    return items.filter((item) => item.type === PropertyType.String);
  }

  private getSectionsForStagedItem(stagedItemType: StagedItemType, item): CombinedSection[] {
    switch (stagedItemType) {
      case StagedItemType.Typecast:
        return this.helpers.getTypecastMenuSections(item) as PropertySection[];
      case StagedItemType.Datetime:
        return this.helpers.getDatetimeMenuSections() as PropertySection[];
      case StagedItemType.Dimension:
        return this.helpers.getDimensionMenuSection(item);
      case StagedItemType.Object:
      case StagedItemType.ComputedListProperty:
      case StagedItemType.ComputedListNumericProperty:
        return this.helpers.getObjectMenuSection();
      case StagedItemType.User:
        return this.getUserMenuSections();
      case StagedItemType.Group:
        return this.helpers.getGroupMenuSection(item);
      case StagedItemType.Cohort:
        return this.helpers.getCohortMenuSection();
      case StagedItemType.BehaviorWithEvent:
        return this.helpers.getBehaviorMenuSection(item);
      case StagedItemType.BrowseProperties:
        return this.getTabbedMenuSections();
      case StagedItemType.BehaviorMultiAttribution:
        return this.getEventsMenuSections({isForMultiAttribution: true, hideSectionLabel: true});
      case StagedItemType.EventWithProperties:
        return this.getEventsMenuSections({item, hideSectionLabel: true});
      default:
        assertUnreachable(stagedItemType);
    }
  }

  async createCustomProperty(resourceType: ResourceType, forBehavioralProperty: boolean) {
    const props = {resourceType};
    const {appliedValue, deletedId, didClose} = await this.promptCustomPropertiesModal(props, forBehavioralProperty);
    if (appliedValue) {
      this.helpers.applyCustomProperty(appliedValue, null);
    } else if (didClose) {
      this.onModalClose();
    } else if (deletedId) {
      this.helpers.deletedCustomProperty();
    }
    this.trackEvent(TrackingEvent.CreateCustomProperty, {
      context: this._context,
      name: appliedValue?.name,
      ...props,
    });
  }

  async createInlineCohort() {
    this.trackEvent(TrackingEvent.OpenInlineCohortsModal, {
      action: `Create`,
      context: this._context,
    });
    const newDefaultCohort = new CohortModel({...new CohortModel(), dataGroupId: this.attr(`data-group-id`)});
    const {cohortStagedValue, cohortSavedValue} = await this.promptInlineCohortsModal(newDefaultCohort);
    this.onModalClose();
    this.handleCohortUpdate(cohortStagedValue, cohortSavedValue);
  }

  set context(value) {
    this._context = value;
  }

  set getSortedMenuSections(getSortedMenuSections) {
    this.update({getSortedMenuSections});
  }

  private dispatchSelectEvent(selectDetails: SelectedPropertyItem): void {
    const selectEvent: SelectPropertyEvent = new CustomEvent(`select`, {detail: selectDetails});
    this.dispatchEvent(selectEvent);
  }

  private dispatchTabChangeEvent(selected: string): void {
    this.dispatchEvent(new CustomEvent(`selectoption`, {detail: {selected}}));
  }

  // Attribute Helpers

  private canAccessUserProperties() {
    return this.attr(`project-has-people`);
  }

  /**
   * TODO for perf purposes, infra team does not want standard people props list available to filter when a
   * B2B data group has been selected for the main analysis. Hopefully we can remove this restriction
   * eventually, as there are legit analysis cases that it enables.
   */
  private shouldShowUserProps() {
    return (
      (this.canAccessUserProperties() || this.attr(`menu-item-types`).includes(MenuItemType.UserProperties)) &&
      !this.hasSelectedDataGroup() &&
      this.state.stagedItemType !== StagedItemType.BehaviorMultiAttribution
    );
  }

  private shouldShowEventProps() {
    return !this.attr(`hide-events`);
  }

  private hasSelectedDataGroup() {
    return this.attr(`all-data-groups`) && !isEmpty(this.attr(`data-group-id`));
  }

  private shouldShowCohorts() {
    return (
      this.attr(`inline-cohorts-override`)?.enable ||
      (this.attr(`project-has-cohorts`) &&
        (!this.hasSelectedDataGroup() || this.attr(`support-b2b-cohorts`)) &&
        this.state.stagedItemType !== StagedItemType.BehaviorMultiAttribution)
    );
  }

  private shouldFilterToEventPropertyIntersection() {
    return (
      this.shouldShowEventProps() &&
      this.attr(`property-combine-mode`) === PropertyCombineMode.Intersection &&
      !isQueryOnAllEvents(this.eventNames())
    );
  }

  private canCreateCustomEventProperties(): boolean {
    return (
      this.shouldShowEventProps() &&
      this.canCreateEntity() &&
      this.attr(`include-custom-properties`) !== IncludeCustomPropertyType.None
    );
  }

  private canCreateCustomUserProperties(): boolean {
    return (
      this.shouldShowUserProps() &&
      this.canCreateEntity() &&
      this.attr(`include-custom-properties`) !== IncludeCustomPropertyType.None
    );
  }

  private canCreateInlineCohorts(): boolean {
    // Create Cohort availability will always be overriden by the inline-cohorts-override settings
    return this.shouldShowCohorts() && Boolean(this.attr(`inline-cohorts-override`)?.enable ?? this.canCreateEntity());
  }

  private canCreateEntity(): boolean {
    if (this.attr(`hide-edit-controls`)) {
      return false;
    }

    if (this.isBetaMenu()) {
      return ![
        StagedItemType.Datetime,
        StagedItemType.Dimension,
        StagedItemType.Object,
        StagedItemType.Typecast,
      ].includes(this.getStagedItemType());
    }
    return true;
  }

  public isUnsortableStage() {
    return (
      (this.hasStagedItem() &&
        ![
          StagedItemType.BehaviorWithEvent,
          StagedItemType.BehaviorMultiAttribution,
          StagedItemType.EventWithProperties,
        ].includes(this.state.stagedItemType)) ||
      this.params.listProperty
    );
  }

  public canSortItems(): boolean {
    // getSortedMenuSections is some pattern to enforce sorting on a query-block
    // this completely contradicts our idea of sorting on behalf of the caller.
    // TODO: Research why we need getSortedMenuSections and if we can pull it out
    return (
      this.isBetaMenu() &&
      !this.state.getSortedMenuSections &&
      !this.isUnsortableStage() &&
      [AllPropertiesTab, `events`].includes(this.state.selectedTab)
    );
  }

  private canWriteDataDefinition() {
    return (
      currentProjectStore.value?.hasPermission(Permission.WriteDataDefinitions) ||
      this.state.permissionList.includes(Permission.WriteDataDefinitions)
    );
  }

  /**
   * This determines if a stagedItemType should be staged as a menu or treated as a selectable item.
   * This logic should be dependent on the context of the menu and not the item itself.
   */
  private canStageItemType(stagedItemType: StagedItemType): boolean {
    switch (stagedItemType) {
      case StagedItemType.Datetime:
        return this.attr(`show-special-date-property-unit`) === true;
      case StagedItemType.Typecast:
        return this.attr(`allow-typecast-pill-action`) === TypecastPillAction.Typecast;
      case StagedItemType.Dimension:
        return !this.isBetaMenu() && !this.attr(`exclude-lookup-table-properties`);
      default:
        return true;
    }
  }

  // @override
  public setBetaVersion({isOn}: {isOn: boolean}) {
    super.setBetaVersion({isOn});
    const tabIsAllowed = this.getToggleTabs().some((tab) => tab.value === this.state.selectedTab);
    if (!tabIsAllowed) {
      this.update({selectedTab: this.getDefaultToggleTab()});
    }
  }

  /**
   * Checks if the selected property is persisted or not
   * in the case for custom properties and inline cohorts
   */
  public isUnsavedCustomEntity(selected: Selection = this.state.selected) {
    const isUnsavedEntity = (item: Item) => {
      if (isInlineCohort(item)) {
        return Boolean(item.value?.cohort?.raw_cohort);
      }

      if (isProperty(item)) {
        return item.customPropertyId && !isPersistedCustomProperty(item.customPropertyId);
      }

      return false;
    };

    if (Array.isArray(selected)) {
      return selected.some(isUnsavedEntity);
    }

    return isUnsavedEntity(selected);
  }

  /**
   * Decides if the apply button should be shown.
   */
  public shouldShowApplyButton() {
    return this.helpers.useMultipleSelection();
  }

  /**
   * Returns display text for the apply button.
   */
  public getApplyButtonText() {
    return this.attr(`apply-button-text`) || `Select ${this.getArraySelection().length}`;
  }

  /**
   * Decides if the apply button should be in a disabled state.
   */
  public isApplyButtonDisabled() {
    const minSelection = this.attr(`min-selection`);

    return this.getArraySelection().length < minSelection;
  }

  /**
   * Decides if the Select All button should be disabled if shown.
   */
  public isSelectAllDisabled() {
    const prop = this.attr(`select-all-disabled`);
    const mechanism = this.attr(`select-all-mechanism`);

    // if prop is explicitly set, use that value
    if (prop) {
      return prop;
    }

    // disable Deselect All if there is none to deselect
    if (mechanism === SelectAllMechanism.Partial) {
      const deselectableItems = this.getArraySelection().filter((item) => !this.isPropertyDisabled(item, item.value));

      return deselectableItems.length === 0;
    }

    return false;
  }

  getComputedListProperties(): PropertySection {
    // prevents nesting computed properties
    if (
      this.state.stagedItemsStack.some((item) =>
        [StagedItemType.ComputedListProperty, StagedItemType.ComputedListNumericProperty].includes(item.stagedItemType),
      )
    ) {
      return null;
    }

    return {
      label: `Computed`,
      items: [LIST_LENGTH, LIST_PROPERTY_SUM, LIST_PROPERTY_UNIQUES].map((item) => ({
        ...item,
        // makes sure for LIST_LENGTH, the resource type matches the list item property
        resourceType: this.params.listProperty.resourceType,
      })),
    };
  }
}

export const PropertiesMenuEl = defineAndMakeJsxFactory<PropertiesMenuJsxProps>(`mp-properties-menu`, PropertiesMenu);
